import React, { StrictMode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { THEME_EVENT, getThemePreference, setThemePreference } from "./theme.js";
import { assistantMessageIntent, authorizationAnalyticsParams, trackAssistantEvent } from "./analytics.js";
import { detectConversationLanguage, draftReviewLanguages, readableTranslationText, translationSourceLanguage } from "./content_languages.js";
import { createRoot } from "react-dom/client";
import {
  AssistantRuntimeProvider,
  AuiIf,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadListItemPrimitive,
  ThreadListPrimitive,
  ThreadPrimitive,
  useAuiState,
  useLocalRuntime,
  useRemoteThreadListRuntime,
} from "@assistant-ui/react";
import { AssistantMarkdown } from "./assistant_markdown.jsx";
import {
  appendSetupListCache,
  readSetupListCache,
  setupListCacheKey,
  writeSetupListCache,
} from "./setup_list_cache";

const csrfToken = () =>
  document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";

// Plain-language fallbacks for responses that carry no `error` message.
const genericErrorMessage = (status) => {
  if (status === 401) return "Your session has expired. Sign in again to continue.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That item could not be found. It may have been removed.";
  if (status === 429) return "Too many requests. Wait a moment and try again.";
  if (status >= 500) return "Something went wrong on our side. Try again in a moment.";
  return "The request could not be completed.";
};

const ensureSentence = (text) => {
  const trimmed = (text ?? "").toString().trim();
  if (!trimmed) return "";
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

// Builds the message shown to the user: what we were doing, then why it failed.
// e.g. describeError("Couldn't load your inbox", error)
//   -> "Couldn't load your inbox. Sign in with Google again to reconnect."
const describeError = (context, error) => {
  const detail = ensureSentence(error?.message);
  const lead = ensureSentence(context);
  if (!lead) return detail;
  if (!detail || detail === lead) return lead;
  return `${lead} ${detail}`;
};

async function fetchJson(url, options = {}) {
  let response;
  try {
    response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        ...(options.headers ?? {}),
      },
    });
  } catch (networkError) {
    if (networkError?.name === "AbortError") throw networkError;
    const error = new Error("PromptMail couldn't be reached. Check your connection and try again.");
    error.network = true;
    error.cause = networkError;
    throw error;
  }
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(payload.error || genericErrorMessage(response.status));
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

const missingPermissionFeature = (error) =>
  error?.payload?.error_code === "additional_permissions_required" ? error.payload.feature : null;

// Starts Google's incremental authorization flow for a feature ("gmail" or
// "chat"). Submits a POST form so omniauth-rails_csrf_protection accepts it.
function requestGoogleFeatureAccess(feature) {
  trackAssistantEvent("google_permission_requested", { feature }, { beacon: true });

  const form = document.createElement("form");
  form.method = "post";
  form.action = `/auth/google_oauth2?feature=${encodeURIComponent(feature)}`;

  const tokenInput = document.createElement("input");
  tokenInput.type = "hidden";
  tokenInput.name = "authenticity_token";
  tokenInput.value = csrfToken();
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
}

const textFromParts = (content = []) => {
  if (typeof content === "string") return content.trim();
  return content
    .filter((part) => part.type === "text")
    .map((part) => part.text)
    .join("\n")
    .trim();
};

const chronologicalValue = (value) => {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const messagesSyncKey = (messages = []) => messages.map((message) => String(message.id)).join("|");

const isDirectMessageSpace = (space) =>
  space?.space_type === "DIRECT_MESSAGE" || space?.type === "DIRECT_MESSAGE";

const spaceDisplayTitle = (space) =>
  space.display_name || space.participant_email || space.name;

const spaceDisplaySubtitle = (space) => {
  if (isDirectMessageSpace(space)) {
    if (space.display_name && space.participant_email) return space.participant_email;
    return "Direct message";
  }

  return space.space_type || space.type;
};

const spaceLatestMessagePreview = (space) => {
  const message = space?.latest_message;
  if (!message) return null;

  return {
    text: message.text || message.formatted_text || "No message preview",
    time: message.create_time,
  };
};

const senderDisplayTitle = (sender) =>
  sender?.display_name || sender?.email || "Unknown";

const messageSenderSubtitle = (sender, timestamp) => {
  if (sender?.display_name && sender?.email) return sender.email;
  return formatTimelineTimestamp(timestamp);
};

const orderConversationsNewestFirst = (conversations, pinnedConversationId = null) =>
  [...conversations].sort((left, right) => {
    if (pinnedConversationId) {
      if (String(left.id) === String(pinnedConversationId)) return -1;
      if (String(right.id) === String(pinnedConversationId)) return 1;
    }

    const rightTimestamp = chronologicalValue(right.updated_at || right.created_at);
    const leftTimestamp = chronologicalValue(left.updated_at || left.created_at);
    return rightTimestamp - leftTimestamp;
  });

const RIGHT_PANE_DEFAULT_WIDTH = 440;
const RIGHT_PANE_MIN_WIDTH = 400;
const RIGHT_PANE_MAX_WIDTH = 640;
const SIDEBAR_WIDTH = 256;
// The chat column never gets squeezed below this while the details pane is docked.
const MAIN_COLUMN_MIN_WIDTH = 640;
const DEFAULT_MAX_SENDS = 3;

// Below these widths the sidebar / right pane become overlays instead of
// docked columns so the chat column always keeps a usable width.
const SIDEBAR_DOCKED_QUERY = "(min-width: 1024px)";
const RIGHT_PANE_DOCKED_QUERY = "(min-width: 1280px)";
const RIGHT_PANE_WIDTH_STORAGE_KEY = "promptmail:rightPaneWidth";
const RIGHT_PANE_COLLAPSED_STORAGE_KEY = "promptmail:rightPaneCollapsed";

const clampWidth = (width) => Math.min(RIGHT_PANE_MAX_WIDTH, Math.max(RIGHT_PANE_MIN_WIDTH, width));

const readStoredRightPaneWidth = () => {
  try {
    const stored = Number(window.localStorage.getItem(RIGHT_PANE_WIDTH_STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? clampWidth(stored) : RIGHT_PANE_DEFAULT_WIDTH;
  } catch {
    return RIGHT_PANE_DEFAULT_WIDTH;
  }
};

const readStoredRightPaneCollapsed = () => {
  try {
    return window.localStorage.getItem(RIGHT_PANE_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeStoredValue = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Storage unavailable; the preference still applies for this page.
  }
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window.matchMedia === "function" ? window.matchMedia(query).matches : true
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(query);
    const update = (event) => setMatches(event.matches);
    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function useViewportWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);

  useEffect(() => {
    const update = () => setWidth(window.innerWidth);
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return width;
}

// User-facing wording for the raw action types and statuses stored by the
// runners. Anything unknown falls back to a humanised version of the key.
const humanizeKey = (value) => {
  const text = (value ?? "").toString().replaceAll("_", " ").trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "";
};

const ACTION_TYPE_LABELS = {
  draft_new_email: "Email drafted",
  draft_reply: "Reply drafted",
  draft_chat_reply: "Chat reply drafted",
  draft_manual_reply: "Reply drafted",
  summarize_conversation: "Summary",
  escalate_for_approval: "Escalated to you",
  authorization_granted: "Autonomous replies turned on",
  authorization_revoked: "Autonomous replies turned off",
  goal_achieved: "Goal achieved",
};

const SEND_ACTION_LABELS = {
  send_new_email: { completed: "Email sent", proposed: "Email waiting for your approval", failed: "Email failed to send", blocked: "Email draft superseded", authorized: "Email approved" },
  send_reply: { completed: "Reply sent", proposed: "Reply waiting for your approval", failed: "Reply failed to send", blocked: "Reply superseded", authorized: "Reply approved" },
  auto_reply: { completed: "Reply sent automatically", proposed: "Reply waiting for your approval", failed: "Automatic reply failed", blocked: "Automatic reply superseded", authorized: "Automatic reply approved" },
  send_chat_reply: { completed: "Chat reply sent", proposed: "Chat reply waiting for your approval", failed: "Chat reply failed to send", blocked: "Chat reply superseded", authorized: "Chat reply approved" },
};

const GOAL_ACTION_LABELS = {
  proposed: "Goal waiting for your approval",
  completed: "Follow-up goal set",
  blocked: "Goal proposal superseded",
  failed: "Goal could not be set",
};

const SILENCE_SEND_LABELS = {
  completed: "Silence follow-up sent",
  proposed: "Silence follow-up waiting for your approval",
  failed: "Silence follow-up failed",
  blocked: "Silence follow-up not sent",
  authorized: "Silence follow-up approved",
};

const actionLabel = (action) => {
  if (!action) return "";
  if (action.payload?.silence_follow_up && action.action_type === "draft_manual_reply") {
    return "Silence follow-up drafted";
  }
  const sendLabels = SEND_ACTION_LABELS[action.action_type];
  if (action.payload?.silence_follow_up && sendLabels && SILENCE_SEND_LABELS[action.status]) {
    return SILENCE_SEND_LABELS[action.status];
  }
  if (sendLabels) return sendLabels[action.status] || humanizeKey(action.action_type);
  if (action.action_type === "set_goal") return GOAL_ACTION_LABELS[action.status] || humanizeKey(action.action_type);
  return ACTION_TYPE_LABELS[action.action_type] || humanizeKey(action.action_type);
};

// Success criteria arrive as a JSON array from the API; anything else is ignored.
const successCriteriaList = (value) =>
  Array.isArray(value) ? value.map((item) => String(item ?? "").trim()).filter(Boolean) : [];

const GOAL_STATUS_LABELS = {
  proposed: "Waiting for your approval",
  completed: "Set",
  blocked: "Superseded",
  failed: "Failed",
};

const goalStatusLabel = (status) => GOAL_STATUS_LABELS[status] || humanizeKey(status);

const ACTION_STATUS_LABELS = {
  proposed: "Needs your approval",
  authorized: "Approved",
  completed: "Done",
  blocked: "Superseded",
  failed: "Failed",
};

const actionStatusLabel = (status) => ACTION_STATUS_LABELS[status] || humanizeKey(status);

const DRAFT_STATUS_LABELS = {
  sent: "Sent",
  "awaiting approval": "Waiting for your approval",
  superseded: "Superseded",
  failed: "Send failed",
  ready: "Ready to copy",
};

const draftStatusLabel = (status) => DRAFT_STATUS_LABELS[status] || humanizeKey(status);

function languageLabel(language) {
  const trimmed = String(language || "").trim();
  if (!trimmed) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function conversationUserLanguage(conversation, actions = []) {
  if (!conversation) return "";

  const messages = conversation.messages ?? [];
  const recorded = [...messages].reverse().find((message) => (
    message?.role === "assistant" && String(message?.metadata?.user_message_language || "").trim()
  ));
  if (recorded) return recorded.metadata.user_message_language;

  const stamped = [...actions]
    .filter((action) => String(action?.payload?.conversation_language || "").trim())
    .sort((left, right) => chronologicalValue(right.created_at) - chronologicalValue(left.created_at))[0];
  if (stamped) return stamped.payload.conversation_language;

  const userText = messages
    .filter((message) => message.role === "user")
    .map((message) => textFromParts(message.content))
    .filter(Boolean)
    .join("\n");
  const assistantText = messages
    .filter((message) => message.role === "assistant")
    .map((message) => textFromParts(message.content))
    .filter(Boolean)
    .join("\n");
  return detectConversationLanguage(userText)
    || detectConversationLanguage(assistantText)
    || detectConversationLanguage(conversation.goal || "")
    || "";
}

function translatableConversationId(conversation) {
  if (!conversation || conversation.pending) return null;
  const id = conversation.id;
  if (id == null || String(id).startsWith("pending-")) return null;
  return id;
}

function plainTextFromHtml(html) {
  return String(html || "")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function emailPlainText(email) {
  const body = String(email?.body_text || "").trim();
  if (body) return body;
  const snippet = String(email?.snippet || "").trim();
  const htmlText = plainTextFromHtml(email?.body_html);
  if (htmlText.length > snippet.length) return htmlText;
  return snippet || htmlText;
}

function readDraftTranslation(payload) {
  if (!payload) return null;
  const content = payload.conversation_language_body || payload.conversation_language_text || "";
  const subject = payload.conversation_language_subject || "";
  if (!String(content).trim() && !String(subject).trim()) return null;
  return { content, subject };
}

function translationReviewNote({ isSent, canApprove, conversationLanguage, draftLanguage }) {
  const conversationLabel = languageLabel(conversationLanguage);
  const draftLabel = languageLabel(draftLanguage);
  if (isSent) {
    return `Shown in ${conversationLabel} for your review. This message was sent in ${draftLabel}.`;
  }
  if (canApprove) {
    return `Shown in ${conversationLabel} for your review. Approve and send uses the original ${draftLabel} draft.`;
  }
  return `Shown in ${conversationLabel} for your review. The draft itself stays in ${draftLabel}.`;
}

const DRAFT_TYPE_LABELS = {
  draft_new_email: "Draft email",
  draft_reply: "Draft reply",
  draft_chat_reply: "Draft chat reply",
  draft_manual_reply: "Draft reply",
};

const CONVERSATION_CHANNEL_LABELS = {
  reply_email: "Gmail reply",
  new_email: "New email",
  reply_chat: "Google Chat",
  manual: "Manual",
};

const conversationChannelLabel = (conversation) =>
  CONVERSATION_CHANNEL_LABELS[conversation?.mode] || "Conversation";

// Shared ChatGPT-style building blocks. Colors come from the semantic tokens
// defined in app/assets/tailwind/application.css (surface, fg, line, accent...).
// Keyboard focus rings come from the global :focus-visible rule in that file.
const BTN_PRIMARY =
  "inline-flex items-center justify-center rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40";
const BTN_PRIMARY_SM =
  "inline-flex items-center justify-center rounded-full bg-accent px-3.5 py-1.5 text-xs font-medium text-accent-fg transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40";
const BTN_SECONDARY =
  "inline-flex items-center justify-center rounded-full border border-line-strong bg-surface px-4 py-2 text-sm font-medium text-fg transition hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40";
const BTN_SECONDARY_SM =
  "inline-flex items-center justify-center rounded-full border border-line-strong bg-surface px-3 py-1.5 text-xs font-medium text-fg transition hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40";
const BTN_LINK_SM = "text-xs font-medium text-fg-secondary underline-offset-4 transition hover:text-fg hover:underline";
const BTN_LINK_13 = "text-[13px]/4 font-medium text-link underline-offset-4 transition hover:underline";
const INPUT_CLASS =
  "w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-fg transition placeholder:text-fg-tertiary";
const SEARCH_INPUT_CLASS =
  "w-full rounded-full border border-line bg-surface py-2 pl-10 pr-4 text-sm text-fg transition placeholder:text-fg-tertiary";
const CARD_CLASS = "rounded-2xl border border-line bg-surface p-4";
const SECTION_LABEL_CLASS = "text-[13px]/4 font-medium text-fg-tertiary";
const ICON_BUTTON_CLASS =
  "flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-fg transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-30";
const GHOST_ICON_BUTTON_CLASS =
  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-fg-secondary transition hover:bg-surface-hover hover:text-fg";
const COMPOSER_SHELL_CLASS =
  "composer-shell flex flex-col rounded-[28px] border border-line bg-surface shadow-composer transition focus-within:border-fg-tertiary";
const INLINE_ERROR_CLASS =
  "flex flex-col gap-3 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm leading-6 text-fg sm:flex-row sm:items-center sm:justify-between";

const segmentClass = (active) =>
  [
    "rounded-full px-4 py-1.5 text-sm font-medium transition",
    active ? "bg-surface text-fg shadow-sm" : "text-fg-secondary hover:text-fg",
  ].join(" ");

const chipClass = (active) =>
  [
    "inline-flex items-center justify-center rounded-full border px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40",
    active
      ? "border-accent bg-accent text-accent-fg"
      : "border-line bg-surface text-fg hover:bg-surface-hover",
  ].join(" ");

const statusBadgeClass = (tone, textSize = "text-xs") =>
  [
    `inline-flex items-center rounded-full px-2.5 py-0.5 font-medium ${textSize}`,
    tone === "success"
      ? "bg-success-soft text-success"
      : tone === "warning"
        ? "bg-warning-soft text-warning"
        : tone === "danger"
          ? "bg-danger-soft text-danger"
          : "bg-surface-tertiary text-fg-secondary",
  ].join(" ");

function Icon({ path, className = "h-4 w-4", strokeWidth = 2 }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {path}
    </svg>
  );
}

const ArrowUpIcon = (props) => (
  <Icon strokeWidth={2.5} path={<><path d="M12 19V5" /><path d="m5 12 7-7 7 7" /></>} {...props} />
);
const StopIcon = ({ className = "h-3.5 w-3.5" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="5" y="5" width="14" height="14" rx="2.5" />
  </svg>
);
const ComposeIcon = (props) => (
  <Icon
    path={<><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>}
    {...props}
  />
);
const TrashIcon = (props) => (
  <Icon
    path={<><path d="M3 6h18" /><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /></>}
    {...props}
  />
);
const SearchIcon = (props) => (
  <Icon path={<><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>} {...props} />
);
const CloseIcon = (props) => <Icon path={<path d="M6 18 18 6M6 6l12 12" />} {...props} />;
const MenuIcon = (props) => <Icon path={<><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>} {...props} />;
const PanelRightIcon = (props) => (
  <Icon path={<><rect x="3" y="4" width="18" height="16" rx="2.5" /><path d="M15 4v16" /></>} {...props} />
);
const CheckIcon = (props) => <Icon path={<path d="m5 12 5 5L20 7" />} {...props} />;
const TargetIcon = (props) => (
  <Icon path={<><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>} {...props} />
);
const AlertIcon = (props) => (
  <Icon path={<><circle cx="12" cy="12" r="9" /><path d="M12 8v4m0 4h.01" /></>} {...props} />
);
const MailIcon = (props) => (
  <Icon
    path={<><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>}
    {...props}
  />
);
const ChatIcon = (props) => (
  <Icon
    path={<path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-4.5A8 8 0 1 1 21 12Z" />}
    {...props}
  />
);
const ClipboardIcon = (props) => (
  <Icon
    path={<><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M8 5H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /></>}
    {...props}
  />
);
const CopyIcon = (props) => (
  <Icon
    path={<><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></>}
    {...props}
  />
);
const ShareIcon = (props) => (
  <Icon
    path={<><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" /></>}
    {...props}
  />
);
const ChevronDownIcon = (props) => <Icon path={<path d="m6 9 6 6 6-6" />} {...props} />;

async function readClipboardText() {
  if (!navigator.clipboard?.readText) {
    throw new Error("This browser didn't share the clipboard. Paste with the keyboard instead.");
  }
  return navigator.clipboard.readText();
}

async function writeClipboardText(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.left = "-9999px";
  document.body.appendChild(area);
  area.select();
  const copied = document.execCommand("copy");
  area.remove();
  if (!copied) throw new Error("Couldn't copy the draft. Select the text and copy it manually.");
}
const LogoMark = ({ className = "h-7 w-7" }) => (
  <span className={["flex items-center justify-center rounded-full bg-[#1E6BFF] text-white", className].join(" ")}>
    <svg className="h-[55%] w-[55%]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 5l7 7-7 7" /><path d="M13 19h6" />
    </svg>
  </span>
);

const SystemThemeIcon = (props) => (
  <Icon path={<><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></>} {...props} />
);
const SunIcon = (props) => (
  <Icon
    path={<><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></>}
    {...props}
  />
);
const MoonIcon = (props) => <Icon path={<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />} {...props} />;

const THEME_OPTIONS = [
  { value: "system", label: "System theme", Icon: SystemThemeIcon },
  { value: "light", label: "Light theme", Icon: SunIcon },
  { value: "dark", label: "Dark theme", Icon: MoonIcon },
];

function useThemePreference() {
  const [preference, setPreference] = useState(() => getThemePreference());

  useEffect(() => {
    const sync = () => setPreference(getThemePreference());
    sync();
    window.addEventListener(THEME_EVENT, sync);
    return () => window.removeEventListener(THEME_EVENT, sync);
  }, []);

  return [preference, setThemePreference];
}

function ThemeSwitch({ className = "" }) {
  const [preference, setPreference] = useThemePreference();

  return (
    <div
      role="radiogroup"
      aria-label="Appearance"
      className={["inline-flex items-center gap-0.5 rounded-full bg-surface-tertiary p-0.5", className].join(" ")}
    >
      {THEME_OPTIONS.map(({ value, label, Icon: OptionIcon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => setPreference(value)}
            className={[
              "inline-flex h-7 w-7 items-center justify-center rounded-full transition",
              active ? "bg-surface text-fg shadow-sm" : "text-fg-tertiary hover:text-fg",
            ].join(" ")}
          >
            <OptionIcon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

const conversationListChatModel = {
  async run() {
    return { content: [{ type: "text", text: "" }] };
  },
};

function useConversationListThreadRuntime() {
  return useLocalRuntime(conversationListChatModel);
}

function ConversationThreadList({
  orderedConversations,
  conversationsById,
  selectedConversation,
  onNewConversation,
  onSelectConversation,
  onDeleteConversation,
  onError,
}) {
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const threadListAdapter = useMemo(() => ({
    async list() {
      return {
        threads: orderedConversations.map((conversation) => ({
          status: "regular",
          remoteId: String(conversation.id),
          title: conversation.title,
          custom: {
            authorizationState: conversation.authorization_state,
          },
        })),
      };
    },
    async fetch(threadId) {
      const conversation = conversationsById.get(String(threadId));
      if (!conversation) throw new Error("Conversation not found.");

      return {
        status: "regular",
        remoteId: String(conversation.id),
        title: conversation.title,
        custom: {
          authorizationState: conversation.authorization_state,
        },
      };
    },
    async initialize(threadId) {
      return { remoteId: threadId, externalId: undefined };
    },
    async rename() {},
    async archive() {},
    async unarchive() {},
    async delete(remoteId) {
      await onDeleteConversation(remoteId);
    },
    async generateTitle() {
      return new ReadableStream();
    },
  }), [orderedConversations, conversationsById, onDeleteConversation]);
  const threadListRuntime = useRemoteThreadListRuntime({
    adapter: threadListAdapter,
    runtimeHook: useConversationListThreadRuntime,
    threadId: selectedConversation ? String(selectedConversation.id) : undefined,
  });
  const conversationOrderById = useMemo(
    () => new Map(orderedConversations.map((conversation, index) => [String(conversation.id), index])),
    [orderedConversations]
  );

  return (
    <AssistantRuntimeProvider runtime={threadListRuntime}>
      <ThreadListPrimitive.Root className="flex min-h-0 flex-1 flex-col">
        <div className="px-2 pt-1">
          <ThreadListPrimitive.New
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium text-fg transition hover:bg-surface-hover"
            onClick={onNewConversation}
          >
            <ComposeIcon className="h-4 w-4 text-fg" />
            New conversation
          </ThreadListPrimitive.New>
        </div>
        <div className="px-4 pb-1 pt-6">
          <h2 className={SECTION_LABEL_CLASS}>Conversations</h2>
        </div>
        <div className="gpt-scroll flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-2">
          {orderedConversations.length === 0 ? (
            <p className="px-2.5 py-2 text-[13px] leading-5 text-fg-tertiary">
              Delegated conversations appear here.
            </p>
          ) : null}
          <ThreadListPrimitive.Items>
            {({ threadListItem }) => {
              const conversation = conversationsById.get(String(threadListItem.remoteId || threadListItem.id));
              const conversationId = conversation ? String(conversation.id) : null;
              const order = conversation ? conversationOrderById.get(conversationId) : orderedConversations.length;
              const confirmingDelete = conversationId && pendingDeleteId === conversationId;
              const isAuthorized =
                (conversation?.authorization_state || threadListItem.custom?.authorizationState) === "authorized";

              // When auto-replies are on that status matters more than the channel,
              // and there is only room for two segments in the sidebar.
              const metaParts = conversation
                ? [
                    isAuthorized ? null : conversationChannelLabel(conversation),
                    formatTimeAgo(conversation.updated_at || conversation.created_at),
                  ].filter(Boolean)
                : [];

              return (
                <ThreadListItemPrimitive.Root
                  className="group relative flex items-center rounded-lg text-fg transition hover:bg-surface-hover data-[active=true]:bg-surface-active"
                  style={{ order }}
                >
                  <ThreadListItemPrimitive.Trigger
                    className="flex min-w-0 flex-1 items-center gap-2 px-2.5 py-2 text-left text-sm"
                    title={conversation?.title || undefined}
                    onClick={() => {
                      if (!conversation) return;
                      setPendingDeleteId(null);
                      onSelectConversation(conversation.id).catch((e) => onError(describeError("Couldn't open that conversation", e)));
                    }}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        <ThreadListItemPrimitive.Title fallback={conversation?.title || "Untitled conversation"} />
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 truncate text-[13px]/4 text-fg-tertiary">
                        {isAuthorized ? (
                          <span className="inline-flex items-center gap-1 text-success">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" aria-hidden="true" />
                            Auto-replies on
                          </span>
                        ) : null}
                        {isAuthorized && metaParts.length ? <span aria-hidden="true">·</span> : null}
                        <span className="truncate">{metaParts.join(" · ")}</span>
                      </span>
                    </span>
                  </ThreadListItemPrimitive.Trigger>
                  {confirmingDelete ? (
                    <div className="mr-1.5 flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="rounded-md px-2 py-1 text-[13px]/4 font-medium text-danger transition hover:bg-danger-soft"
                        aria-label={`Confirm delete ${conversation?.title || "conversation"}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          setPendingDeleteId(null);
                          onDeleteConversation(conversationId).catch((e) => onError(describeError("Couldn't remove that conversation", e)));
                        }}
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        className="rounded-md p-1 text-fg-tertiary transition hover:bg-surface-active hover:text-fg"
                        aria-label="Cancel delete"
                        onClick={(event) => {
                          event.stopPropagation();
                          setPendingDeleteId(null);
                        }}
                      >
                        <CloseIcon className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="mr-1.5 rounded-md p-1 text-fg-tertiary opacity-0 transition hover:bg-surface-active hover:text-fg focus-visible:opacity-100 group-hover:opacity-100 group-data-[active=true]:opacity-100"
                      aria-label={`Delete ${conversation?.title || "conversation"}`}
                      disabled={!conversationId}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!conversationId) return;
                        setPendingDeleteId(conversationId);
                      }}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                </ThreadListItemPrimitive.Root>
              );
            }}
          </ThreadListPrimitive.Items>
        </div>
      </ThreadListPrimitive.Root>
    </AssistantRuntimeProvider>
  );
}

function SidebarUserMenu({ user }) {
  const initial = (user.name || user.email || "?").trim().charAt(0).toUpperCase();

  return (
    <div className="shrink-0 border-t border-line/60 p-2">
      <div className="flex items-center justify-between gap-2 px-2 pb-1 pt-1">
        <span className={SECTION_LABEL_CLASS}>Appearance</span>
        <ThemeSwitch />
      </div>
      <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-tertiary text-xs font-semibold text-fg">
            {initial}
          </span>
        )}
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-sm font-medium text-fg">{user.name || user.email}</p>
          {user.name && user.email ? (
            <p className="truncate text-[13px]/4 text-fg-tertiary">{user.email}</p>
          ) : null}
        </div>
        <form method="post" action={user.signOutPath} data-turbo="false" className="shrink-0">
          <input type="hidden" name="_method" value="delete" />
          <input type="hidden" name="authenticity_token" value={csrfToken()} />
          <button
            type="submit"
            className="rounded-md p-1.5 text-fg-tertiary transition hover:bg-surface-hover hover:text-fg"
            title="Sign out"
            aria-label="Sign out"
          >
            <Icon
              className="h-4 w-4"
              path={<><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>}
            />
          </button>
        </form>
      </div>
    </div>
  );
}

function SidebarContent({ currentUser, threadListOrderKey, orderedConversations, conversationsById, selectedConversation, onNewConversation, onSelectConversation, onDeleteConversation, onError, onClose }) {
  return (
    <>
      <div className="flex h-14 shrink-0 items-center justify-between px-3">
        <a
          href={currentUser.homePath}
          className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm font-semibold text-fg transition hover:bg-surface-hover"
        >
          <LogoMark className="h-6 w-6" />
          PromptMail
        </a>
        {onClose ? (
          <button type="button" className={GHOST_ICON_BUTTON_CLASS} onClick={onClose} aria-label="Close conversations menu">
            <CloseIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>
      <ConversationThreadList
        key={threadListOrderKey}
        orderedConversations={orderedConversations}
        conversationsById={conversationsById}
        selectedConversation={selectedConversation}
        onNewConversation={onNewConversation}
        onSelectConversation={onSelectConversation}
        onDeleteConversation={onDeleteConversation}
        onError={onError}
      />
      <SidebarUserMenu user={currentUser} />
    </>
  );
}

function MainHeader({ title, showMenuButton, onOpenMenu, showDetailsButton, detailsOpen, detailsAttention, onToggleDetails, detailsLabel }) {
  return (
    <div className="flex h-14 shrink-0 items-center gap-2 border-b border-line/60 px-3 sm:px-4 xl:border-transparent">
      {showMenuButton ? (
        <button type="button" className={GHOST_ICON_BUTTON_CLASS} onClick={onOpenMenu} aria-label="Open conversations menu">
          <MenuIcon className="h-5 w-5" />
        </button>
      ) : null}
      <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-fg" title={title}>
        {title}
      </h2>
      {showDetailsButton ? (
        <button
          type="button"
          className={[GHOST_ICON_BUTTON_CLASS, "relative w-auto gap-2 px-2.5"].join(" ")}
          onClick={onToggleDetails}
          aria-pressed={detailsOpen}
          aria-label={detailsLabel}
          title={detailsLabel}
        >
          <PanelRightIcon className="h-4 w-4" />
          <span className="hidden text-sm font-medium sm:inline">Details</span>
          {detailsAttention ? (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-warning" aria-hidden="true" />
          ) : null}
        </button>
      ) : null}
    </div>
  );
}

function OverlayPanel({ side, label, onClose, children, widthClass }) {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex" role="dialog" aria-modal="true" aria-label={label}>
      <button
        type="button"
        className="absolute inset-0 bg-black/40 focus-ring-none"
        aria-label={`Close ${label.toLowerCase()}`}
        onClick={onClose}
      />
      <div
        className={[
          "relative flex h-full min-h-0 flex-col bg-surface shadow-2xl",
          side === "left" ? "mr-auto" : "ml-auto",
          widthClass,
        ].join(" ")}
      >
        {children}
      </div>
    </div>
  );
}

function EmailAgentApp({ rootElement }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [actions, setActions] = useState([]);
  const [error, setError] = useState("");
  const [pinnedConversationId, setPinnedConversationId] = useState(null);
  const [rightPaneWidth, setRightPaneWidth] = useState(readStoredRightPaneWidth);
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(readStoredRightPaneCollapsed);
  const [rightPaneResize, setRightPaneResize] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPaneOpen, setRightPaneOpen] = useState(false);
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);
  const [autonomousRules, setAutonomousRules] = useState(null);
  const [setupPreviewEmail, setSetupPreviewEmail] = useState(null);
  const [setupPreviewChat, setSetupPreviewChat] = useState(null);
  const rightPaneResizeRef = useRef(null);
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;
  const isSidebarDocked = useMediaQuery(SIDEBAR_DOCKED_QUERY);
  const isRightPaneDocked = useMediaQuery(RIGHT_PANE_DOCKED_QUERY);
  const viewportWidth = useViewportWidth();
  const gmailConnected = rootElement.dataset.gmailConnected === "true";
  const currentUser = {
    name: rootElement.dataset.userName || "",
    email: rootElement.dataset.userEmail || "",
    avatarUrl: rootElement.dataset.userAvatarUrl || "",
    signOutPath: rootElement.dataset.signOutPath || "/sign_out",
    homePath: rootElement.dataset.homePath || "/",
  };
  const [permissions, setPermissions] = useState({
    gmail: rootElement.dataset.gmailAuthorized === "true",
    chat: rootElement.dataset.chatAuthorized === "true",
  });
  // One-shot URL parameters, stripped from the address bar once read so a
  // reload does not replay them:
  // - ?prompt=...   handed over from the marketing page; pre-fills the setup
  //                 composer.
  // - ?connected=…  set by the OAuth callback after an incremental Gmail /
  //                 Chat grant; opens the setup screen on that service so its
  //                 inbox / spaces load right away.
  // - ?service=manual, ?shared_text=, ?shared_url=  open Manual mode,
  //                 including text shared in from another app.
  // Any of these keeps the setup screen in front instead of auto-opening the
  // newest conversation.
  const [{ initialPrompt, connectedFeature, initialServiceType, initialManualText, initialManualCounterpart, keepSetupOpen }] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const prompt = (params.get("prompt") || "").trim();
    const connected = params.get("connected");
    const feature = connected === "gmail" || connected === "chat" ? connected : null;
    const sharedText = [params.get("shared_text"), params.get("shared_url")]
      .map((value) => (value || "").trim())
      .filter(Boolean)
      .join("\n\n");
    const sharedTitle = (params.get("title") || "").trim();
    const sharedIntoManual = params.get("service") === "manual" || sharedText.length > 0;
    const gmailAuthorized = rootElement.dataset.gmailAuthorized === "true";
    const chatAuthorized = rootElement.dataset.chatAuthorized === "true";
    let serviceType = "email";
    if (sharedIntoManual) serviceType = "manual";
    else if (feature === "chat") serviceType = "chat";
    else if (feature === "gmail" || gmailAuthorized) serviceType = "email";
    else if (chatAuthorized) serviceType = "chat";
    else serviceType = "manual";

    const launchParams = ["prompt", "connected", "service", "shared_text", "shared_url", "title"];
    if (launchParams.some((key) => params.has(key))) {
      launchParams.forEach((key) => params.delete(key));
      const query = params.toString();
      window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    }
    const counterpart = sharedTitle && !sharedTitle.includes("://") ? sharedTitle.slice(0, 120) : "";
    return {
      initialPrompt: prompt,
      connectedFeature: feature,
      initialServiceType: serviceType,
      initialManualText: sharedText,
      initialManualCounterpart: counterpart,
      keepSetupOpen: Boolean(prompt || feature || sharedIntoManual),
    };
  });

  useEffect(() => {
    if (assistantPageTracked) return;
    assistantPageTracked = true;
    trackAssistantEvent("assistant_opened", {
      gmail_connected: gmailConnected,
      gmail_authorized: permissions.gmail,
      chat_authorized: permissions.chat,
    });
    if (connectedFeature) {
      trackAssistantEvent("google_permission_granted", { feature: connectedFeature });
    }
  }, [gmailConnected, permissions.gmail, permissions.chat, connectedFeature]);

  const markFeatureUnauthorized = useCallback((feature) => {
    if (feature !== "gmail" && feature !== "chat") return;
    setPermissions((current) => (current[feature] ? { ...current, [feature]: false } : current));
  }, []);

  const resizeRightPaneToClientX = useCallback((clientX) => {
    const resize = rightPaneResizeRef.current;
    if (!resize || clientX === 0) return;

    const delta = clientX - resize.startX;
    setRightPaneWidth(clampWidth(resize.startWidth - delta));
  }, []);

  const finishRightPaneResize = useCallback(() => {
    rightPaneResizeRef.current = null;
    setRightPaneResize(null);
  }, []);

  useEffect(() => {
    writeStoredValue(RIGHT_PANE_WIDTH_STORAGE_KEY, rightPaneWidth);
  }, [rightPaneWidth]);

  useEffect(() => {
    writeStoredValue(RIGHT_PANE_COLLAPSED_STORAGE_KEY, rightPaneCollapsed);
  }, [rightPaneCollapsed]);

  // Overlays only make sense while the pane is undocked.
  useEffect(() => {
    if (isSidebarDocked) setSidebarOpen(false);
  }, [isSidebarDocked]);

  useEffect(() => {
    if (isRightPaneDocked) setRightPaneOpen(false);
  }, [isRightPaneDocked]);

  const loadConversations = async ({ autoSelect = true } = {}) => {
    const payload = await fetchJson("/api/agent/conversations");
    const nextConversations = orderConversationsNewestFirst(payload.conversations ?? []);
    setConversations(nextConversations);

    if (autoSelect && !selectedConversation && nextConversations.length > 0) {
      selectConversation(nextConversations[0].id).catch((e) => setError(describeError("Couldn't open your latest conversation", e)));
    }
  };

  const syncConversationInList = useCallback((conversation) => {
    setConversations((current) =>
      orderConversationsNewestFirst(
        current.map((item) =>
          String(item.id) === String(conversation.id) ? { ...item, ...conversation } : item
        ),
        pinnedConversationId
      )
    );
  }, [pinnedConversationId]);

  const selectConversation = async (id) => {
    const conversation = await fetchJson(`/api/agent/conversations/${id}`);
    setSetupPreviewEmail(null);
    setSetupPreviewChat(null);
    setSelectedConversation(conversation);
    setActions(conversation.actions ?? []);
    setPinnedConversationId(null);
    syncConversationInList(conversation);
    setSidebarOpen(false);
    return conversation;
  };

  const deleteConversation = useCallback(async (id) => {
    const mode = conversationsRef.current.find((conversation) => String(conversation.id) === String(id))?.mode;
    await fetchJson(`/api/agent/conversations/${id}`, { method: "DELETE" });
    trackAssistantEvent("conversation_deleted", { mode });
    setPinnedConversationId((current) => (String(current) === String(id) ? null : current));
    setConversations((current) => current.filter((conversation) => String(conversation.id) !== String(id)));

    if (selectedConversation && String(selectedConversation.id) === String(id)) {
      setSelectedConversation(null);
      setActions([]);
    }
  }, [selectedConversation]);

  useEffect(() => {
    loadConversations({ autoSelect: !keepSetupOpen }).catch((e) => setError(describeError("Couldn't load your conversations", e)));
  }, []);

  useEffect(() => {
    fetchJson("/api/agent/autonomous_rules")
      .then(setAutonomousRules)
      .catch((e) => setError(describeError("Couldn't load your standing rules", e)));
  }, []);

  // Autonomous runs happen in background jobs, so the open conversation's
  // messages and actions are re-fetched periodically to surface auto-replies
  // and escalations without a manual refresh.
  const selectedConversationId = selectedConversation?.pending ? null : selectedConversation?.id ?? null;

  useEffect(() => {
    if (selectedConversationId == null) return undefined;

    let cancelled = false;
    let inFlight = false;

    const pollConversation = () => {
      if (inFlight || document.hidden) return;
      inFlight = true;

      fetchJson(`/api/agent/conversations/${selectedConversationId}`)
        .then((conversation) => {
          if (cancelled) return;
          setSelectedConversation((current) =>
            current && String(current.id) === String(conversation.id) ? conversation : current
          );
          setActions(conversation.actions ?? []);
          syncConversationInList(conversation);
        })
        // Quiet by design: the next tick retries, and a toast every 30s would be noise.
        .catch(() => {})
        .finally(() => {
          inFlight = false;
        });
    };

    const intervalId = window.setInterval(pollConversation, CONVERSATION_POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", pollConversation);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", pollConversation);
    };
  }, [selectedConversationId, syncConversationInList]);

  useEffect(() => {
    if (!rightPaneResize) return;

    const originalCursor = document.body.style.cursor;
    const originalUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handlePointerMove = (event) => resizeRightPaneToClientX(event.clientX);
    const handlePointerUp = () => finishRightPaneResize();

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);

    return () => {
      document.body.style.cursor = originalCursor;
      document.body.style.userSelect = originalUserSelect;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("mouseup", handlePointerUp);
    };
  }, [finishRightPaneResize, resizeRightPaneToClientX, rightPaneResize]);

  const handleCreated = (conversation) => {
    setSetupPreviewEmail(null);
    setSetupPreviewChat(null);
    setPinnedConversationId(conversation.id);
    setConversations((current) => orderConversationsNewestFirst([
      conversation,
      ...current.filter((item) => item.id !== conversation.id),
    ], conversation.id));
    setSelectedConversation(conversation);
    setActions(conversation.actions ?? []);
  };

  const handleCreating = (conversation) => {
    setSetupPreviewEmail(null);
    setSetupPreviewChat(null);
    setPinnedConversationId(conversation.id);
    setSelectedConversation(conversation);
    setActions([]);
  };

  const handleCreateFailed = () => {
    setPinnedConversationId(null);
    setSelectedConversation(null);
    setActions([]);
  };

  const handleAgentResult = (result) => {
    if (result.actions?.length) {
      setActions((current) => [...result.actions, ...current]);
    }

    loadConversations().catch(() => {});

    const nextAuthorizationState = result.conversation?.authorization_state;
    const goalChanged = result.conversation &&
      (result.conversation.goal ?? null) !== (selectedConversation?.goal ?? null);
    const authorizationChanged = nextAuthorizationState &&
      nextAuthorizationState !== selectedConversation?.authorization_state;

    if ((goalChanged || authorizationChanged) && selectedConversation?.id) {
      fetchJson(`/api/agent/conversations/${selectedConversation.id}`)
        .then((conversation) => {
          setSelectedConversation(conversation);
          setActions(conversation.actions ?? []);
          setThreadRefreshKey((current) => current + 1);
        })
        .catch(() => {
          if (result.conversation) {
            setSelectedConversation((current) => current && {
              ...current,
              ...result.conversation,
            });
          }
          setThreadRefreshKey((current) => current + 1);
        });
      return;
    }

    if (result.conversation) {
      setSelectedConversation((current) => current && {
        ...current,
        ...result.conversation,
      });
    }

    setThreadRefreshKey((current) => current + 1);
  };

  const handleDraftTranslated = (updatedAction) => {
    if (!updatedAction?.id) return;

    setActions((current) => current.map((action) => (
      String(action.id) === String(updatedAction.id) ? { ...action, ...updatedAction } : action
    )));
  };

  const refreshSelectedConversation = () => {
    if (!selectedConversation) return Promise.resolve();

    return selectConversation(selectedConversation.id)
      .then(() => setThreadRefreshKey((current) => current + 1))
      .catch((e) => setError(describeError("Couldn't refresh this conversation", e)));
  };

  const orderedConversations = useMemo(
    () => orderConversationsNewestFirst(conversations, pinnedConversationId),
    [conversations, pinnedConversationId]
  );
  const conversationsById = useMemo(
    () => new Map(orderedConversations.map((conversation) => [String(conversation.id), conversation])),
    [orderedConversations]
  );
  const threadListOrderKey = orderedConversations.map((conversation) => conversation.id).join(":");
  // The stored preference is kept as-is; only the rendered width is capped so a
  // wide pane saved on a large monitor doesn't crush the chat on a laptop.
  const maxDockedRightPaneWidth = Math.max(
    RIGHT_PANE_MIN_WIDTH,
    viewportWidth - (isSidebarDocked ? SIDEBAR_WIDTH : 0) - MAIN_COLUMN_MIN_WIDTH,
  );
  const dockedRightPaneWidth = Math.min(rightPaneWidth, maxDockedRightPaneWidth);
  const beginRightPaneResize = useCallback((event, options = { preventDefault: true }) => {
    if (options.preventDefault) event.preventDefault();

    const resize = {
      startX: event.clientX,
      startWidth: dockedRightPaneWidth,
    };
    rightPaneResizeRef.current = resize;
    setRightPaneResize(resize);
  }, [dockedRightPaneWidth]);
  const resizeRightPaneBy = useCallback((delta) => {
    setRightPaneWidth((current) => clampWidth(current + delta));
  }, []);

  const startNewConversation = () => {
    setSelectedConversation(null);
    setActions([]);
    setSetupPreviewEmail(null);
    setSetupPreviewChat(null);
    setSidebarOpen(false);
  };

  const handleManualTranscript = (conversation) => {
    if (!conversation?.id) return;
    setSelectedConversation((current) => {
      if (!current || String(current.id) !== String(conversation.id)) return current;
      return {
        ...current,
        ...conversation,
        messages: conversation.messages ?? current.messages,
      };
    });
    if (conversation.actions) setActions(conversation.actions);
    syncConversationInList(conversation);
    setThreadRefreshKey((current) => current + 1);
  };

  const showRightPaneDocked = isRightPaneDocked && !rightPaneCollapsed;
  const gridTemplateColumns = [
    isSidebarDocked ? `${SIDEBAR_WIDTH}px` : null,
    "minmax(0, 1fr)",
    showRightPaneDocked ? `${dockedRightPaneWidth}px` : null,
  ].filter(Boolean).join(" ");

  const detailsOpen = isRightPaneDocked ? !rightPaneCollapsed : rightPaneOpen;
  const detailsLabel = detailsOpen ? "Hide conversation details" : "Show conversation details";
  const toggleDetails = () => {
    if (isRightPaneDocked) {
      setRightPaneCollapsed((current) => !current);
    } else {
      setRightPaneOpen((current) => !current);
    }
  };
  const detailsAttention = actions.some(isProposedSendAction);
  const mainTitle = selectedConversation ? selectedConversation.title : "New conversation";
  const showGmailBanner = !gmailConnected && isEmailMode(selectedConversation?.mode);

  const sidebarContent = (
    <SidebarContent
      currentUser={currentUser}
      threadListOrderKey={threadListOrderKey}
      orderedConversations={orderedConversations}
      conversationsById={conversationsById}
      selectedConversation={selectedConversation}
      onNewConversation={startNewConversation}
      onSelectConversation={selectConversation}
      onDeleteConversation={deleteConversation}
      onError={setError}
      onClose={isSidebarDocked ? null : () => setSidebarOpen(false)}
    />
  );

  const rightPane = (
    <RightPane
      conversation={selectedConversation}
      previewEmail={selectedConversation ? null : setupPreviewEmail}
      previewChat={selectedConversation ? null : setupPreviewChat}
      actions={actions}
      autonomousRules={autonomousRules}
      onRulesSaved={setAutonomousRules}
      onAuthorizationChanged={refreshSelectedConversation}
      onManualTranscriptChanged={handleManualTranscript}
      onError={setError}
      threadRefreshKey={threadRefreshKey}
      docked={isRightPaneDocked}
      onClose={isRightPaneDocked ? null : () => setRightPaneOpen(false)}
      isResizing={Boolean(rightPaneResize)}
      onResizeStart={beginRightPaneResize}
      onResizeMove={resizeRightPaneToClientX}
      onResizeEnd={finishRightPaneResize}
      onResizeBy={resizeRightPaneBy}
    />
  );

  return (
    <div
      className="grid h-full min-h-0 overflow-hidden bg-surface font-sans text-fg"
      style={{ gridTemplateColumns }}
    >
      {isSidebarDocked ? (
        <aside className="flex min-h-0 flex-col bg-surface-secondary">{sidebarContent}</aside>
      ) : null}

      <main className="relative flex min-h-0 min-w-0 flex-col bg-surface">
        <MainHeader
          title={mainTitle}
          showMenuButton={!isSidebarDocked}
          onOpenMenu={() => setSidebarOpen(true)}
          showDetailsButton
          detailsOpen={detailsOpen}
          detailsAttention={detailsAttention}
          onToggleDetails={toggleDetails}
          detailsLabel={detailsLabel}
        />

        {(error || (showGmailBanner)) && (
          <div className="pointer-events-none absolute inset-x-0 top-16 z-20 flex flex-col items-center gap-2 px-4">
            {error && (
              <div
                role="alert"
                className="pointer-events-auto flex w-full max-w-xl items-start gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-sm leading-6 text-fg shadow-lg"
              >
                <AlertIcon className="mt-1 h-4 w-4 shrink-0 text-danger" />
                <span className="min-w-0 flex-1">{error}</span>
                <button
                  type="button"
                  className="shrink-0 rounded-full p-1 text-fg-tertiary transition hover:bg-surface-hover hover:text-fg"
                  onClick={() => setError("")}
                  aria-label="Dismiss error"
                >
                  <CloseIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {showGmailBanner && (
              <div className="pointer-events-auto flex w-full max-w-xl items-start gap-3 rounded-2xl border border-line bg-surface px-4 py-3 text-sm leading-6 text-fg shadow-lg">
                <Icon
                  className="mt-1 h-4 w-4 shrink-0 text-warning"
                  path={<><path d="M12 9v4m0 4h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /></>}
                />
                <span>Gmail is not connected. Sign in with Google again before authorizing sends.</span>
              </div>
            )}
          </div>
        )}

        {selectedConversation ? (
          <EmailAgentThread
            key={`${selectedConversation.id}-${selectedConversation.goal ? "goal" : "no-goal"}-${selectedConversation.authorization_state}`}
            conversation={selectedConversation}
            actions={actions}
            onAgentResult={handleAgentResult}
            onActionApproved={refreshSelectedConversation}
            onDraftTranslated={handleDraftTranslated}
            onError={setError}
          />
        ) : (
          <ConversationSetup
            permissions={permissions}
            cacheScope={rootElement.dataset.userEmail}
            initialInstructionText={initialPrompt}
            initialServiceType={initialServiceType}
            initialManualText={initialManualText}
            initialManualCounterpart={initialManualCounterpart}
            freshlyConnectedFeature={connectedFeature}
            onFeatureUnauthorized={markFeatureUnauthorized}
            onCreating={handleCreating}
            onCreated={handleCreated}
            onCreateFailed={handleCreateFailed}
            onPreviewEmailChange={setSetupPreviewEmail}
            onPreviewChatChange={setSetupPreviewChat}
            onError={setError}
          />
        )}
      </main>

      {showRightPaneDocked ? rightPane : null}

      {!isSidebarDocked && sidebarOpen ? (
        <OverlayPanel side="left" label="Conversations" onClose={() => setSidebarOpen(false)} widthClass="w-72 max-w-[85vw] bg-surface-secondary">
          {sidebarContent}
        </OverlayPanel>
      ) : null}

      {!isRightPaneDocked && rightPaneOpen ? (
        <OverlayPanel side="right" label="Conversation details" onClose={() => setRightPaneOpen(false)} widthClass="w-[30rem] max-w-full">
          {rightPane}
        </OverlayPanel>
      ) : null}
    </div>
  );
}

const isEmailMode = (mode) => mode === "reply_email" || mode === "new_email";
const isChatMode = (mode) => mode === "reply_chat";
const isManualMode = (mode) => mode === "manual";
const isManualDraftAction = (action) => action?.action_type === "draft_manual_reply";

const THREAD_POLL_INTERVAL_MS = 30_000;
const CONVERSATION_POLL_INTERVAL_MS = 30_000;

const conversationHasGmailThread = (conversation) =>
  isEmailMode(conversation?.mode) && Boolean(conversation?.selected_thread_id);

const conversationShowsThreadEmails = (conversation) =>
  conversation?.mode === "reply_email" || (conversation?.mode === "new_email" && conversation?.selected_thread_id);

const SET_FOLLOW_UP_GOAL_LABEL = "Set a follow-up goal";
const SET_FOLLOW_UP_GOAL_PROMPT = [
  "Set a follow-up goal for this conversation.",
  "",
  "Infer the outcome I most likely want from the selected email or chat and our conversation. Always ask me at least one question before setting the goal—even if only to confirm your inferred goal. Ask further clarification questions when the answer would change the goal itself, not for details an agent can obtain while pursuing it. Ask one question at a time, with short options when the alternatives are clear.",
  "",
  "Only after I answer, propose a concise well-defined goal (one or two sentences) with 2–5 observable success criteria for my approval. Do not include strategy, tone, or authorization. Do not ask me to write the goal myself.",
].join("\n");

const SETUP_EXAMPLE_PROMPTS = {
  reply_email: [
    {
      label: "Explain this email",
      prompt: "Explain this email. What is it about, what are they asking, and what should I know before I reply?",
    },
    {
      label: "Suggest a reply",
      prompt: "Suggest a reply to this email, and explain why this reply is a good response.",
    },
    {
      label: SET_FOLLOW_UP_GOAL_LABEL,
      prompt: SET_FOLLOW_UP_GOAL_PROMPT,
    },
  ],
  new_email: [],
  reply_chat: [
    {
      label: "Explain this message",
      prompt: "Explain this conversation. What is it about, what are they asking, and what should I know before I reply?",
    },
    {
      label: "Suggest a reply",
      prompt: "Suggest a reply to this conversation, and explain why this reply is a good response.",
    },
    {
      label: SET_FOLLOW_UP_GOAL_LABEL,
      prompt: SET_FOLLOW_UP_GOAL_PROMPT,
    },
  ],
  manual: [
    {
      label: "Explain this message",
      prompt: "Explain this conversation. What is it about, what are they asking, and what should I know before I reply?",
    },
    {
      label: "Suggest a reply",
      prompt: "Suggest a reply to this conversation, and explain why this reply is a good response.",
    },
    {
      label: SET_FOLLOW_UP_GOAL_LABEL,
      prompt: SET_FOLLOW_UP_GOAL_PROMPT,
    },
  ],
};

function ConversationSetup({
  permissions,
  cacheScope,
  initialInstructionText = "",
  initialServiceType = "email",
  freshlyConnectedFeature = null,
  onFeatureUnauthorized,
  onCreating,
  onCreated,
  onCreateFailed,
  initialManualText = "",
  initialManualCounterpart = "",
  onPreviewEmailChange,
  onPreviewChatChange,
  onError,
}) {
  const [serviceType, setServiceType] = useState(initialServiceType);
  const [mode, setMode] = useState(
    initialServiceType === "chat" ? "reply_chat" : initialServiceType === "manual" ? "manual" : "reply_email"
  );
  // Right after an incremental grant, any cached list predates the new
  // tokens (e.g. from before access was revoked), so the first load of that
  // service bypasses the cache.
  const freshConnectionRef = useRef(freshlyConnectedFeature);
  const consumeFreshConnection = (feature) => {
    if (freshConnectionRef.current !== feature) return false;
    freshConnectionRef.current = null;
    return true;
  };
  const [initialInstruction, setInitialInstruction] = useState(initialInstructionText);
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [inbox, setInbox] = useState([]);
  const [inboxNextPageToken, setInboxNextPageToken] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingInbox, setLoadingInbox] = useState(false);
  const [loadingMoreInbox, setLoadingMoreInbox] = useState(false);
  const [inboxSearch, setInboxSearch] = useState("");
  const [inboxLoaded, setInboxLoaded] = useState(false);
  const [inboxUpdatedAt, setInboxUpdatedAt] = useState(null);
  const [inboxError, setInboxError] = useState("");
  const [spacesError, setSpacesError] = useState("");
  const [activeInboxQuery, setActiveInboxQuery] = useState("");
  const instructionRef = useRef(null);
  const inboxListRef = useRef(null);
  const inboxSentinelRef = useRef(null);
  const inboxNextPageTokenRef = useRef(null);
  const inboxLoadingRef = useRef(false);
  // Mirrors `inbox` so async appends can merge without a setState updater.
  const inboxRef = useRef([]);
  // Query used to load the current result set; appends (infinite scroll) must
  // keep using it even if the user has since edited the search input.
  const inboxQueryRef = useRef("");

  const [spaces, setSpaces] = useState([]);
  const [spacesNextPageToken, setSpacesNextPageToken] = useState(null);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [loadingMoreSpaces, setLoadingMoreSpaces] = useState(false);
  const [spacesLoaded, setSpacesLoaded] = useState(false);
  const [spacesUpdatedAt, setSpacesUpdatedAt] = useState(null);
  const [spacesHint, setSpacesHint] = useState("");
  const [spacesSearch, setSpacesSearch] = useState("");
  const [activeSpacesQuery, setActiveSpacesQuery] = useState("");
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);
  const spacesListRef = useRef(null);
  const spacesSentinelRef = useRef(null);
  const spacesNextPageTokenRef = useRef(null);
  const spacesLoadingRef = useRef(false);
  const spacesRef = useRef([]);
  const spacesHintRef = useRef("");
  // Same role as inboxQueryRef: infinite-scroll appends must reuse the query
  // that produced the current result set, not whatever is in the input now.
  const spacesQueryRef = useRef("");

  const [now, setNow] = useState(() => Date.now());
  const [counterpart, setCounterpart] = useState(initialManualCounterpart);
  const [manualBody, setManualBody] = useState(initialManualText);

  useEffect(() => {
    if (!inboxUpdatedAt && !spacesUpdatedAt) return undefined;

    const intervalId = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(intervalId);
  }, [inboxUpdatedAt, spacesUpdatedAt]);

  useEffect(() => {
    inboxRef.current = inbox;
  }, [inbox]);

  useEffect(() => {
    spacesRef.current = spaces;
    spacesHintRef.current = spacesHint;
  }, [spaces, spacesHint]);

  useEffect(() => {
    onPreviewEmailChange?.(mode === "reply_email" ? selectedEmail : null);
  }, [mode, selectedEmail, onPreviewEmailChange]);

  useEffect(() => {
    if (mode === "reply_chat" && selectedSpace) {
      onPreviewChatChange?.({
        space: selectedSpace,
        messages: chatMessages,
        loading: loadingChatMessages,
      });
    } else {
      onPreviewChatChange?.(null);
    }
  }, [mode, selectedSpace, chatMessages, loadingChatMessages, onPreviewChatChange]);

  useEffect(() => () => {
    onPreviewEmailChange?.(null);
    onPreviewChatChange?.(null);
  }, [onPreviewEmailChange, onPreviewChatChange]);

  const filledManualMessages = manualBody.trim()
    ? [{
        direction: "inbound",
        sender_name: counterpart.trim(),
        body: manualBody.trim(),
      }]
    : [];

  const pasteManualMessage = async () => {
    try {
      const text = await readClipboardText();
      if (!text.trim()) {
        onError("The clipboard is empty.");
        return;
      }
      setManualBody((current) => (current.trim() ? `${current.replace(/\s+$/, "")}\n${text}` : text));
    } catch (e) {
      onError(describeError("Couldn't read the clipboard. Paste with the keyboard instead", e));
    }
  };

  const handleServiceTypeChange = (newServiceType) => {
    setServiceType(newServiceType);
    onError(""); // Clear any previous error when switching service types
    if (newServiceType === "email") {
      setMode("reply_email");
      setSelectedSpace(null);
      setChatMessages([]);
    } else if (newServiceType === "chat") {
      setMode("reply_chat");
      setSelectedEmail(null);
    } else {
      setMode("manual");
      setSelectedEmail(null);
      setSelectedSpace(null);
      setChatMessages([]);
    }
  };

  // Cached lists are reused until Refresh (`force: true`) or a new search.
  const loadInbox = useCallback(async ({ append = false, query, force = false } = {}) => {
    if (inboxLoadingRef.current) return;
    if (append && !inboxNextPageTokenRef.current) return;

    if (!append) {
      inboxQueryRef.current = (query ?? "").trim();

      const cached = force ? null : readSetupListCache(setupListCacheKey(cacheScope, "inbox", inboxQueryRef.current));
      if (cached) {
        setInbox(cached.emails);
        inboxNextPageTokenRef.current = cached.nextPageToken;
        setInboxNextPageToken(cached.nextPageToken);
        setInboxLoaded(true);
        setInboxUpdatedAt(cached.loadedAt);
        setNow(Date.now());
        setActiveInboxQuery(inboxQueryRef.current);
        setInboxError("");
        return;
      }
    }

    inboxLoadingRef.current = true;
    if (append) {
      setLoadingMoreInbox(true);
    } else {
      inboxNextPageTokenRef.current = null;
      setInboxNextPageToken(null);
      setLoadingInbox(true);
    }

    try {
      const params = new URLSearchParams({ max_results: "10" });
      if (inboxQueryRef.current) params.set("query", inboxQueryRef.current);
      if (append) params.set("page_token", inboxNextPageTokenRef.current);

      const payload = await fetchJson(`/api/agent/inbox?${params.toString()}`);
      const emails = payload.emails ?? [];
      const nextToken = payload.next_page_token ?? null;
      const cacheKey = setupListCacheKey(cacheScope, "inbox", inboxQueryRef.current);

      let nextInbox = emails;
      if (append) {
        const seen = new Set(inboxRef.current.map((email) => email.id));
        nextInbox = [...inboxRef.current, ...emails.filter((email) => !seen.has(email.id))];
        appendSetupListCache(cacheKey, { emails: nextInbox, nextPageToken: nextToken });
      } else {
        const entry = writeSetupListCache(cacheKey, { emails: nextInbox, nextPageToken: nextToken });
        setInboxUpdatedAt(entry.loadedAt);
        setNow(entry.loadedAt);
      }
      setInbox(nextInbox);
      inboxNextPageTokenRef.current = nextToken;
      setInboxNextPageToken(nextToken);
      setInboxLoaded(true);
      setActiveInboxQuery(inboxQueryRef.current);
      setInboxError("");
    } catch (e) {
      const feature = missingPermissionFeature(e);
      if (feature) {
        onFeatureUnauthorized(feature);
      } else {
        // Shown inline in the inbox card (with a retry) rather than as a toast.
        setInboxError(describeError(append ? "Couldn't load more emails" : "Couldn't load your inbox", e));
      }
    } finally {
      inboxLoadingRef.current = false;
      setLoadingInbox(false);
      setLoadingMoreInbox(false);
    }
  }, [cacheScope, onFeatureUnauthorized]);

  const searchInbox = useCallback(() => {
    loadInbox({ query: inboxSearch });
  }, [loadInbox, inboxSearch]);

  const clearInboxSearch = useCallback(() => {
    setInboxSearch("");
    loadInbox({ query: "" });
  }, [loadInbox]);

  useEffect(() => {
    if (mode !== "reply_email" || !permissions.gmail) return undefined;
    loadInbox({ query: "", force: consumeFreshConnection("gmail") });
    return undefined;
  }, [mode, permissions.gmail, loadInbox]);

  useEffect(() => {
    const root = inboxListRef.current;
    const sentinel = inboxSentinelRef.current;
    if (!root || !sentinel || !inboxNextPageToken) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadInbox({ append: true });
        }
      },
      { root, rootMargin: "80px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [inboxNextPageToken, loadInbox]);

  // Cached lists are reused until Refresh (`force: true`) or a new search.
  const loadSpaces = useCallback(async ({ append = false, query, force = false } = {}) => {
    if (spacesLoadingRef.current) return;
    if (append && !spacesNextPageTokenRef.current) return;

    if (!append) {
      spacesQueryRef.current = (query ?? "").trim();

      const cached = force ? null : readSetupListCache(setupListCacheKey(cacheScope, "spaces", spacesQueryRef.current));
      if (cached) {
        setSpaces(cached.spaces);
        spacesNextPageTokenRef.current = cached.nextPageToken;
        setSpacesNextPageToken(cached.nextPageToken);
        setSpacesLoaded(true);
        setSpacesUpdatedAt(cached.loadedAt);
        setNow(Date.now());
        setActiveSpacesQuery(spacesQueryRef.current);
        setSpacesHint(cached.spacesHint);
        setSpacesError("");
        return;
      }
    }

    spacesLoadingRef.current = true;
    if (append) {
      setLoadingMoreSpaces(true);
    } else {
      spacesNextPageTokenRef.current = null;
      setSpacesNextPageToken(null);
      setLoadingSpaces(true);
    }

    try {
      const params = new URLSearchParams({ page_size: "20" });
      if (spacesQueryRef.current) params.set("query", spacesQueryRef.current);
      if (append) params.set("page_token", spacesNextPageTokenRef.current);

      const payload = await fetchJson(`/api/agent/spaces?${params.toString()}`);
      const nextSpaces = payload.spaces ?? [];
      const nextToken = payload.next_page_token ?? null;
      const cacheKey = setupListCacheKey(cacheScope, "spaces", spacesQueryRef.current);

      let mergedSpaces = nextSpaces;
      let nextHint = payload.spaces_hint || "";
      if (append) {
        const seen = new Set(spacesRef.current.map((space) => space.name));
        mergedSpaces = [...spacesRef.current, ...nextSpaces.filter((space) => !seen.has(space.name))];
        nextHint = payload.spaces_hint || spacesHintRef.current;
        appendSetupListCache(cacheKey, { spaces: mergedSpaces, nextPageToken: nextToken, spacesHint: nextHint });
      } else {
        const entry = writeSetupListCache(cacheKey, { spaces: mergedSpaces, nextPageToken: nextToken, spacesHint: nextHint });
        setSpacesUpdatedAt(entry.loadedAt);
        setNow(entry.loadedAt);
      }
      setSpaces(mergedSpaces);
      spacesNextPageTokenRef.current = nextToken;
      setSpacesNextPageToken(nextToken);
      setSpacesLoaded(true);
      setActiveSpacesQuery(spacesQueryRef.current);
      setSpacesHint(nextHint);
      setSpacesError("");
    } catch (e) {
      const feature = missingPermissionFeature(e);
      if (feature) {
        onFeatureUnauthorized(feature);
      } else {
        setSpacesError(describeError(append ? "Couldn't load more spaces" : "Couldn't load your Google Chat spaces", e));
      }
    } finally {
      spacesLoadingRef.current = false;
      setLoadingSpaces(false);
      setLoadingMoreSpaces(false);
    }
  }, [cacheScope, onFeatureUnauthorized]);

  const searchSpaces = useCallback(() => {
    loadSpaces({ query: spacesSearch });
  }, [loadSpaces, spacesSearch]);

  const clearSpacesSearch = useCallback(() => {
    setSpacesSearch("");
    loadSpaces({ query: "" });
  }, [loadSpaces]);

  useEffect(() => {
    if (!isChatMode(mode) || !permissions.chat) return undefined;
    loadSpaces({ force: consumeFreshConnection("chat") });
    return undefined;
  }, [mode, permissions.chat, loadSpaces]);

  useEffect(() => {
    const root = spacesListRef.current;
    const sentinel = spacesSentinelRef.current;
    if (!root || !sentinel || !spacesNextPageToken) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadSpaces({ append: true });
        }
      },
      { root, rootMargin: "80px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [spacesNextPageToken, loadSpaces]);

  const loadChatMessages = async (spaceName) => {
    setLoadingChatMessages(true);
    try {
      const payload = await fetchJson(`/api/agent/chat_messages/${spaceName}?page_size=1`);
      setChatMessages(payload.messages ?? []);
    } catch (e) {
      const feature = missingPermissionFeature(e);
      if (feature) {
        onFeatureUnauthorized(feature);
      } else {
        onError(describeError("Couldn't load the latest message in that space", e));
      }
    } finally {
      setLoadingChatMessages(false);
    }
  };

  const handleSpaceSelect = (space) => {
    setSelectedSpace(space);
    setChatMessages([]);
    if (mode === "reply_chat") {
      loadChatMessages(space.name);
    }
  };

  const createConversation = async (event) => {
    event.preventDefault();
    const submittedInstruction = initialInstruction.trim();

    try {
      let conversationPayload;

      if (isEmailMode(mode)) {
        conversationPayload = {
          mode,
          recipient: mode === "new_email" ? recipient : undefined,
          subject: mode === "new_email" ? subject : selectedEmail?.subject,
          selected_message_id: mode === "reply_email" ? selectedEmail?.id : undefined,
          selected_thread_id: mode === "reply_email" ? selectedEmail?.thread_id : undefined,
          selected_email_snapshot: mode === "reply_email" ? selectedEmail : undefined,
        };
      } else if (isManualMode(mode)) {
        conversationPayload = {
          mode,
          recipient: counterpart.trim(),
          manual_messages: filledManualMessages,
        };
      } else {
        conversationPayload = {
          mode,
          selected_space_name: selectedSpace?.name,
        };
      }

      const now = new Date().toISOString();
      const pendingConversation = {
        id: `pending-${Date.now()}`,
        title: submittedInstruction.slice(0, 60),
        mode,
        recipient: conversationPayload.recipient,
        subject: conversationPayload.subject,
        selected_message_id: conversationPayload.selected_message_id,
        selected_thread_id: conversationPayload.selected_thread_id,
        selected_email_snapshot: conversationPayload.selected_email_snapshot,
        selected_space_name: conversationPayload.selected_space_name,
        manual_channel: conversationPayload.manual_channel,
        manual_messages: conversationPayload.manual_messages,
        goal: null,
        goal_planned: false,
        authorization_state: "unauthorized",
        authorization_limits: {},
        last_summary: "Starting conversation...",
        created_at: now,
        updated_at: now,
        actions: [],
        pending: true,
        messages: [
          {
            id: `pending-user-${Date.now()}`,
            role: "user",
            content: [{ type: "text", text: submittedInstruction }],
            created_at: now,
          },
        ],
      };

      onCreating(pendingConversation);

      const conversation = await fetchJson("/api/agent/conversations", {
        method: "POST",
        body: JSON.stringify({ conversation: conversationPayload, message: submittedInstruction }),
      });
      trackAssistantEvent("conversation_started", { mode });
      onCreated(conversation);
    } catch (e) {
      onCreateFailed();
      onError(describeError("Couldn't start the conversation", e));
    }
  };

  const canSubmit = () => {
    if (!initialInstruction.trim()) return false;
    if (mode === "new_email") return recipient.trim();
    if (mode === "reply_email") return selectedEmail;
    if (mode === "reply_chat") return selectedSpace;
    if (mode === "manual") return counterpart.trim();
    return false;
  };

  const getInstructionPlaceholder = () => {
    if (mode === "reply_email") return "Tell the assistant what to do with this email…";
    if (mode === "new_email") return "Tell the assistant what this email should achieve…";
    if (mode === "manual") return "Tell the assistant what to do with this message…";
    return "Tell the assistant what to do in this space…";
  };

  const examplePrompts = SETUP_EXAMPLE_PROMPTS[mode] ?? [];
  const useExamplePrompt = (prompt) => {
    setInitialInstruction(prompt);
    instructionRef.current?.focus();
  };

  const selectionHint = mode === "reply_email"
    ? selectedEmail
      ? `Replying to: ${selectedEmail.subject || "(No subject)"}`
      : "Select an inbox email to reply to"
    : mode === "new_email"
      ? recipient.trim()
        ? `New email to ${recipient.trim()}`
        : "Add a recipient for the new email"
      : mode === "manual"
        ? counterpart.trim()
          ? `New message from ${counterpart.trim()}`
          : "Add who the message is from"
        : selectedSpace
          ? `Replying in: ${spaceDisplayTitle(selectedSpace)}`
          : "Select a Google Chat space";

  return (
    <form className="gpt-scroll flex min-h-0 flex-1 flex-col overflow-y-auto" onSubmit={createConversation}>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pb-10 pt-8 sm:px-6 sm:pt-12">
      <div className="text-center">
        <h2 className="text-2xl font-semibold tracking-tight text-fg sm:text-3xl">Start a conversation</h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-fg-secondary">
          Pick an email or chat space, then tell the assistant what you want: ask a question, request a reply, or authorize an agent to pursue a goal on its own.
        </p>
      </div>

      <div className="flex justify-center">
        <div className="inline-flex max-w-full flex-wrap justify-center rounded-full bg-surface-tertiary p-1" role="tablist" aria-label="Channel">
          <button
            type="button"
            role="tab"
            aria-selected={serviceType === "email"}
            className={[segmentClass(serviceType === "email"), "inline-flex items-center gap-2"].join(" ")}
            onClick={() => handleServiceTypeChange("email")}
          >
            <MailIcon className="h-4 w-4" />
            Gmail
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={serviceType === "chat"}
            className={[segmentClass(serviceType === "chat"), "inline-flex items-center gap-2"].join(" ")}
            onClick={() => handleServiceTypeChange("chat")}
          >
            <ChatIcon className="h-4 w-4" />
            Google Chat
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={serviceType === "manual"}
            className={[segmentClass(serviceType === "manual"), "inline-flex items-center gap-2"].join(" ")}
            onClick={() => handleServiceTypeChange("manual")}
          >
            <ClipboardIcon className="h-4 w-4" />
            Manual
          </button>
        </div>
      </div>

      {serviceType === "email" && !permissions.gmail && (
        <FeaturePermissionPrompt
          feature="gmail"
          title="Connect Gmail"
          description="PromptMail needs your permission to read and send Gmail messages before it can load your inbox. Google will ask you to approve Gmail access."
          buttonLabel="Grant Gmail access"
        />
      )}

      {serviceType === "email" && permissions.gmail && (
        <div className={CARD_CLASS}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-fg">{mode === "new_email" ? "New email" : "Inbox"}</h3>
              {mode === "reply_email" && inboxUpdatedAt ? (
                <p className="mt-0.5 text-xs text-fg-tertiary">
                  Updated {formatTimeAgo(inboxUpdatedAt, now)}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {mode === "reply_email" ? (
                <>
                  <button
                    type="button"
                    className={chipClass(false)}
                    onClick={() => {
                      setMode("new_email");
                      setSelectedEmail(null);
                    }}
                  >
                    New email
                  </button>
                  <button
                    type="button"
                    className={chipClass(false)}
                    onClick={() => loadInbox({ query: activeInboxQuery || "", force: true })}
                    disabled={loadingInbox}
                  >
                    {loadingInbox ? "Loading..." : inboxLoaded ? "Refresh" : "Load inbox"}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className={chipClass(false)}
                  onClick={() => setMode("reply_email")}
                >
                  Back to inbox
                </button>
              )}
            </div>
          </div>

          {mode === "reply_email" ? (
            <>
              <div className="mb-3 flex gap-2">
                <div className="relative flex-1">
                  <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-tertiary" />
                  <input
                    type="search"
                    className={SEARCH_INPUT_CLASS}
                    placeholder="Search inbox, e.g. invoice or from:alice@example.com"
                    aria-label="Search inbox"
                    value={inboxSearch}
                    onChange={(event) => setInboxSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        searchInbox();
                      }
                    }}
                  />
                </div>
                <button
                  type="button"
                  className={BTN_SECONDARY}
                  onClick={() => searchInbox()}
                >
                  Search
                </button>
              </div>
              {inboxLoaded && activeInboxQuery ? (
                <div className="mb-2 flex items-center justify-between px-1 text-xs text-fg-secondary">
                  <span>
                    Showing results for <span className="font-medium text-fg">"{activeInboxQuery}"</span>
                  </span>
                  <button type="button" className={BTN_LINK_SM} onClick={clearInboxSearch}>
                    Clear search
                  </button>
                </div>
              ) : null}
              {inboxError ? (
                <div className={["mb-3", INLINE_ERROR_CLASS].join(" ")} role="alert">
                  <span className="flex items-start gap-2">
                    <AlertIcon className="mt-1 h-4 w-4 shrink-0 text-danger" />
                    <span>{inboxError}</span>
                  </span>
                  <button
                    type="button"
                    className={[BTN_SECONDARY_SM, "shrink-0"].join(" ")}
                    onClick={() => loadInbox({ query: activeInboxQuery || inboxQueryRef.current || "", force: true })}
                    disabled={loadingInbox}
                  >
                    {loadingInbox ? "Retrying…" : "Try again"}
                  </button>
                </div>
              ) : null}
              <div
                ref={inboxListRef}
                className="gpt-scroll -mx-1 max-h-72 space-y-0.5 overflow-y-auto px-1"
                onScroll={(event) => {
                  const root = event.currentTarget;
                  if (root.scrollHeight - root.scrollTop - root.clientHeight < 80) {
                    loadInbox({ append: true });
                  }
                }}
              >
                {loadingInbox && inbox.length === 0 ? (
                  <p className="py-6 text-center text-xs text-fg-tertiary">Loading your inbox…</p>
                ) : null}
                {inboxLoaded && !loadingInbox && inbox.length === 0 ? (
                  <p className="py-6 text-center text-xs text-fg-tertiary">
                    {activeInboxQuery ? `No emails match "${activeInboxQuery}".` : "Your inbox is empty."}
                  </p>
                ) : null}
                {inbox.map((email) => {
                  const listTimestamp = formatInboxListTimestamp(email.date);
                  const selected = selectedEmail?.id === email.id;

                  return (
                  <button
                    type="button"
                    key={email.id}
                    aria-pressed={selected}
                    onClick={() => setSelectedEmail(email)}
                    className={[
                      "w-full rounded-xl px-3 py-2.5 text-left text-sm transition",
                      selected ? "bg-surface-tertiary ring-1 ring-line-strong" : "hover:bg-surface-hover",
                    ].join(" ")}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="block truncate font-medium text-fg">{email.subject || "(No subject)"}</span>
                      {listTimestamp ? (
                        <time className="shrink-0 text-xs text-fg-tertiary">{listTimestamp}</time>
                      ) : null}
                    </div>
                    <span className="block truncate text-xs text-fg-secondary">{email.from}</span>
                    <span className="mt-1 block text-xs text-fg-tertiary line-clamp-2">{email.snippet}</span>
                  </button>
                  );
                })}
                {inboxNextPageToken ? (
                  <div ref={inboxSentinelRef} className="py-2 text-center text-xs text-fg-tertiary">
                    {loadingMoreInbox ? "Loading more..." : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-fg-secondary">Recipient</span>
                <input
                  className={["mt-1", INPUT_CLASS].join(" ")}
                  value={recipient}
                  onChange={(event) => setRecipient(event.target.value)}
                  placeholder="alice@example.com"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-fg-secondary">Subject</span>
                <input
                  className={["mt-1", INPUT_CLASS].join(" ")}
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  placeholder="Project update"
                />
              </label>
            </div>
          )}
        </div>
      )}

      {isChatMode(mode) && !permissions.chat && (
        <FeaturePermissionPrompt
          feature="chat"
          title="Connect Google Chat"
          description="PromptMail needs your permission to read and send Google Chat messages before it can load your spaces. Google will ask you to approve Chat access."
          buttonLabel="Grant Google Chat access"
          variant="chat"
        />
      )}

      {isChatMode(mode) && permissions.chat && (
        <div className={CARD_CLASS}>
          <div className="mb-3 flex items-center justify-between">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-fg">Chat spaces</h3>
              {spacesUpdatedAt ? (
                <p className="mt-0.5 text-xs text-fg-tertiary">
                  Updated {formatTimeAgo(spacesUpdatedAt, now)}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className={chipClass(false)}
              onClick={() => loadSpaces({ query: activeSpacesQuery || "", force: true })}
              disabled={loadingSpaces}
            >
              {loadingSpaces ? "Loading..." : spacesLoaded ? "Refresh" : "Load spaces"}
            </button>
          </div>
          <div className="mb-3 flex gap-2">
            <div className="relative flex-1">
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-tertiary" />
              <input
                type="search"
                className={SEARCH_INPUT_CLASS}
                placeholder="Search spaces by name or person, e.g. design or alice@example.com"
                aria-label="Search spaces"
                value={spacesSearch}
                onChange={(event) => setSpacesSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    searchSpaces();
                  }
                }}
              />
            </div>
            <button
              type="button"
              className={BTN_SECONDARY}
              onClick={() => searchSpaces()}
            >
              Search
            </button>
          </div>
          {spacesLoaded && activeSpacesQuery ? (
            <div className="mb-2 flex items-center justify-between px-1 text-xs text-fg-secondary">
              <span>
                Showing spaces matching <span className="font-medium text-fg">"{activeSpacesQuery}"</span>
              </span>
              <button type="button" className={BTN_LINK_SM} onClick={clearSpacesSearch}>
                Clear search
              </button>
            </div>
          ) : null}
          {spacesHint ? (
            <p className="mb-3 rounded-xl bg-warning-soft px-3 py-2 text-xs text-warning">{spacesHint}</p>
          ) : null}
          {spacesError ? (
            <div className={["mb-3", INLINE_ERROR_CLASS].join(" ")} role="alert">
              <span className="flex items-start gap-2">
                <AlertIcon className="mt-1 h-4 w-4 shrink-0 text-danger" />
                <span>{spacesError}</span>
              </span>
              <button
                type="button"
                className={[BTN_SECONDARY_SM, "shrink-0"].join(" ")}
                onClick={() => loadSpaces({ query: activeSpacesQuery || spacesQueryRef.current || "", force: true })}
                disabled={loadingSpaces}
              >
                {loadingSpaces ? "Retrying…" : "Try again"}
              </button>
            </div>
          ) : null}
          <div
            ref={spacesListRef}
            className="gpt-scroll -mx-1 max-h-72 space-y-0.5 overflow-y-auto px-1"
            onScroll={(event) => {
              const root = event.currentTarget;
              if (root.scrollHeight - root.scrollTop - root.clientHeight < 80) {
                loadSpaces({ append: true });
              }
            }}
          >
            {loadingSpaces && spaces.length === 0 ? (
              <p className="py-6 text-center text-xs text-fg-tertiary">Loading your spaces…</p>
            ) : null}
            {spacesLoaded && !loadingSpaces && spaces.length === 0 && !spacesNextPageToken ? (
              <p className="py-6 text-center text-xs text-fg-tertiary">
                {activeSpacesQuery ? `No spaces match "${activeSpacesQuery}".` : "No spaces found."}
              </p>
            ) : null}
            {spaces.map((space) => {
              const preview = spaceLatestMessagePreview(space);
              const listTimestamp = formatInboxListTimestamp(preview?.time);
              const selected = selectedSpace?.name === space.name;

              return (
                <button
                  type="button"
                  key={space.name}
                  aria-pressed={selected}
                  onClick={() => handleSpaceSelect(space)}
                  className={[
                    "w-full rounded-xl px-3 py-2.5 text-left text-sm transition",
                    selected ? "bg-surface-tertiary ring-1 ring-line-strong" : "hover:bg-surface-hover",
                  ].join(" ")}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="block truncate font-medium text-fg">{spaceDisplayTitle(space)}</span>
                    {listTimestamp ? (
                      <time className="shrink-0 text-xs text-fg-tertiary">{listTimestamp}</time>
                    ) : null}
                  </div>
                  <span className="block truncate text-xs text-fg-secondary">{spaceDisplaySubtitle(space)}</span>
                  {preview?.text ? (
                    <span className="mt-1 block text-xs text-fg-tertiary line-clamp-2">{preview.text}</span>
                  ) : null}
                </button>
              );
            })}
            {spacesNextPageToken ? (
              <div ref={spacesSentinelRef} className="py-2 text-center text-xs text-fg-tertiary">
                {loadingMoreSpaces ? (activeSpacesQuery ? "Searching more spaces..." : "Loading more...") : null}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {isManualMode(mode) && (
        <div className={CARD_CLASS}>
          <h3 className="mb-3 text-sm font-semibold text-fg">New message</h3>
          <label className="block">
            <span className="text-xs font-medium text-fg-secondary">From</span>
            <input
              className={["mt-1", INPUT_CLASS].join(" ")}
              value={counterpart}
              maxLength={120}
              onChange={(event) => setCounterpart(event.target.value)}
              placeholder="Alex"
            />
          </label>
          <div className="mt-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-fg-secondary">Message</span>
              <button type="button" className={BTN_LINK_SM} onClick={pasteManualMessage}>
                Paste
              </button>
            </div>
            <textarea
              className={["mt-1 gpt-scroll min-h-20", INPUT_CLASS].join(" ")}
              value={manualBody}
              onChange={(event) => setManualBody(event.target.value)}
              placeholder="See you Thursday?"
              aria-label="Message"
            />
          </div>
        </div>
      )}

      <div>
        <label className="block">
          <span className="sr-only">What would you like the assistant to do?</span>
          <div className={[COMPOSER_SHELL_CLASS, "p-3"].join(" ")}>
            <textarea
              ref={instructionRef}
              className="gpt-scroll max-h-52 min-h-20 w-full resize-none bg-transparent px-2 pt-1 text-base leading-6 text-fg outline-none placeholder:text-fg-tertiary"
              value={initialInstruction}
              onChange={(event) => setInitialInstruction(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
                event.preventDefault();
                if (!canSubmit()) return;
                event.currentTarget.form?.requestSubmit();
              }}
              placeholder={getInstructionPlaceholder()}
            />
            <div className="mt-2 flex items-center justify-between gap-3 px-1">
              <span className="truncate text-xs text-fg-tertiary">{selectionHint}</span>
              <button
                type="submit"
                className={ICON_BUTTON_CLASS}
                disabled={!canSubmit()}
                aria-label="Start conversation"
                title="Start conversation"
              >
                <ArrowUpIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </label>
        {examplePrompts.length ? (
          <ul className="mt-3 flex flex-wrap justify-center gap-2" aria-label="Example requests">
            {examplePrompts.map((example) => (
              <li key={example.label}>
                <button type="button" className={chipClass(false)} onClick={() => useExamplePrompt(example.prompt)}>
                  {example.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="mt-4 text-center text-[13px]/4 text-fg-tertiary">
          Nothing is sent until you approve it or authorize autonomous follow-up.
        </p>
      </div>
      </div>
    </form>
  );
}

function FeaturePermissionPrompt({ feature, title, description, buttonLabel, variant = "email" }) {
  const isChat = variant === "chat";

  return (
    <div className="rounded-2xl border border-line bg-surface-secondary p-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-tertiary text-fg">
          {isChat ? <ChatIcon className="h-4 w-4" /> : <MailIcon className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-fg">{title}</h3>
          <p className="mt-1 text-sm leading-6 text-fg-secondary">{description}</p>
          <button
            type="button"
            className={["mt-3", BTN_PRIMARY].join(" ")}
            onClick={() => requestGoogleFeatureAccess(feature)}
          >
            {buttonLabel}
          </button>
          <p className="mt-2 text-xs text-fg-tertiary">
            You'll be redirected to Google to approve, then brought back here.
          </p>
        </div>
      </div>
    </div>
  );
}

function EmailAgentThread({ conversation, actions = [], onAgentResult, onActionApproved, onDraftTranslated, onError }) {
  const chatModel = useMemo(() => ({
    async run({ messages, abortSignal }) {
      if (conversation.pending) {
        throw new Error("The assistant is still starting this conversation.");
      }

      const latestUserMessage = [...messages].reverse().find((message) => message.role === "user");
      const messageText = textFromParts(latestUserMessage?.content);
      const payload = await fetchJson(`/api/agent/conversations/${conversation.id}/run`, {
        method: "POST",
        body: JSON.stringify({ message: messageText }),
        signal: abortSignal,
      });

      trackAssistantEvent("assistant_message_sent", {
        mode: conversation.mode,
        intent: assistantMessageIntent(messageText, SET_FOLLOW_UP_GOAL_PROMPT),
      });
      onAgentResult(payload);
      return {
        content: payload.message?.content ?? [{ type: "text", text: "I updated this conversation." }],
      };
    },
  }), [conversation.id, conversation.pending, onAgentResult]);

  const initialMessages = useMemo(() => {
    const messages = [...(conversation.messages ?? [])].map((message) => ({
      ...message,
      createdAt: message.createdAt || message.created_at,
    }));

    if (conversation.pending) {
      messages.push({
        id: `pending-assistant-${conversation.id}`,
        role: "assistant",
        content: [],
        status: "running",
        createdAt: conversation.updated_at ?? new Date().toISOString(),
      });
    }

    return messages;
  }, [conversation]);

  const draftItems = useMemo(() => buildConversationDraftItems(actions), [actions]);
  const goalItems = useMemo(() => buildConversationGoalItems(actions), [actions]);
  const userLanguage = useMemo(
    () => conversationUserLanguage(conversation, actions),
    [conversation, actions],
  );

  const runtime = useLocalRuntime(chatModel, { initialMessages });

  // useLocalRuntime only reads initialMessages once, so messages persisted after
  // mount (autonomous replies, escalations) are pushed into the thread here. The
  // server is the source of truth: once it knows messages the thread does not,
  // the thread is reset to the server list. Skipped while a turn is running so
  // the optimistic user/assistant messages are not replaced mid-stream.
  const syncedMessagesKeyRef = useRef(messagesSyncKey(initialMessages));

  useEffect(() => {
    if (conversation.pending) return;

    const nextKey = messagesSyncKey(initialMessages);
    if (nextKey === syncedMessagesKeyRef.current) return;

    const threadState = runtime.thread.getState();
    if (threadState.isRunning) return;

    const knownIds = new Set(threadState.messages.map((message) => String(message.id)));
    const hasUnseenMessages = initialMessages.some((message) => !knownIds.has(String(message.id)));

    if (hasUnseenMessages) runtime.thread.reset(initialMessages);
    syncedMessagesKeyRef.current = nextKey;
  }, [conversation.pending, initialMessages, runtime]);

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
        <ThreadPrimitive.Viewport className="gpt-scroll min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 pb-6 pt-6">
            <ConversationFeed
              draftItems={draftItems}
              goalItems={goalItems}
              conversation={conversation}
              userLanguage={userLanguage}
              onActionApproved={onActionApproved}
              onDraftTranslated={onDraftTranslated}
              onError={onError}
            />
          </div>
        </ThreadPrimitive.Viewport>
        <div className="shrink-0 px-3 pb-3 sm:px-4">
          <div className="mx-auto w-full max-w-3xl">
            {conversation.pending ? (
              <div className="flex items-center gap-3 rounded-[28px] border border-line bg-surface-tertiary px-5 py-4 text-sm text-fg-secondary">
                <AssistantLoadingDots />
                Starting conversation...
              </div>
            ) : (
              <AgentComposer mode={conversation.mode} />
            )}
            <p className="pt-2 text-center text-[13px]/4 text-fg-tertiary">
              Nothing is sent until you approve it or authorize autonomous follow-up.
            </p>
          </div>
        </div>
      </ThreadPrimitive.Root>
    </AssistantRuntimeProvider>
  );
}

function ConversationFeed({ draftItems, goalItems = [], conversation, userLanguage = "", onActionApproved, onDraftTranslated, onError }) {
  const messages = useAuiState((state) => state.thread.messages);

  const feedItems = useMemo(() => {
    const entries = messages.map((message, index) => ({
      kind: "message",
      id: `message-${message.id}`,
      sortValue: chronologicalValue(message.createdAt),
      index,
    }));

    draftItems.forEach((item) => {
      entries.push({
        kind: "draft",
        id: item.id,
        sortValue: chronologicalValue(item.timestamp),
        item,
      });
    });

    goalItems.forEach((item) => {
      entries.push({
        kind: "goal",
        id: item.id,
        sortValue: chronologicalValue(item.timestamp),
        item,
      });
    });

    return entries.sort((left, right) => {
      if (left.sortValue !== right.sortValue) return left.sortValue - right.sortValue;
      if (left.kind !== right.kind) return left.kind === "message" ? -1 : 1;
      return (left.index ?? 0) - (right.index ?? 0);
    });
  }, [messages, draftItems, goalItems]);

  return feedItems.map((entry) => {
    if (entry.kind === "goal") {
      return (
        <ConversationGoalCard
          key={entry.id}
          proposal={entry.item.proposal}
          canApprove={entry.item.canApprove}
          conversation={conversation}
          onActionApproved={onActionApproved}
          onError={onError}
        />
      );
    }

    if (entry.kind === "draft") {
      return (
        <ConversationDraftCard
          key={entry.id}
          draft={entry.item.draft}
          sendApproval={entry.item.sendApproval}
          sendAction={entry.item.sendAction}
          canApprove={entry.item.canApprove}
          canCopy={entry.item.canCopy}
          statusLabel={entry.item.statusLabel}
          conversation={conversation}
          userLanguage={userLanguage}
          onActionApproved={onActionApproved}
          onDraftTranslated={onDraftTranslated}
          onError={onError}
        />
      );
    }

    return (
      <ThreadPrimitive.MessageByIndex
        key={entry.id}
        index={entry.index}
        components={{ Message: AgentMessage }}
      />
    );
  });
}

// Criteria are plain bullets while the goal is open; once the goal is achieved
// each criterion is shown ticked.
function SuccessCriteriaList({
  criteria,
  className = "",
  textClass = "text-sm leading-6 text-fg-secondary",
  achieved = false,
}) {
  const items = successCriteriaList(criteria);
  if (items.length === 0) return null;

  return (
    <div className={className}>
      <p className={SECTION_LABEL_CLASS}>Success criteria</p>
      <ul className="mt-1 space-y-1" aria-label="Success criteria">
        {items.map((criterion, index) => (
          <li key={`${index}-${criterion}`} className={["flex items-start gap-2", textClass].join(" ")}>
            {achieved ? (
              <CheckIcon className="mt-1.5 h-3.5 w-3.5 shrink-0 text-success" aria-label="Met" />
            ) : (
              <span className="w-3.5 shrink-0 select-none text-center" aria-hidden="true">•</span>
            )}
            <span className="min-w-0 whitespace-pre-wrap">{criterion}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FollowUpGoalDisplay({ goal, successCriteria = [], achieved = false, achievedAt = null, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  const text = goal || NO_GOAL_LABEL;
  const canToggle = Boolean(goal);
  const criteria = goal ? successCriteriaList(successCriteria) : [];
  const isAchieved = Boolean(goal) && achieved;
  const expandLabel = "Show full goal";

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2">
        <p className={SECTION_LABEL_CLASS}>Follow-up goal</p>
        {isAchieved ? (
          <span
            className={statusBadgeClass("success", "text-[11px]/4")}
            title={achievedAt ? `Achieved ${formatTimelineTimestamp(achievedAt)}` : "Achieved"}
          >
            Achieved
          </span>
        ) : null}
      </div>
      {canToggle && !expanded ? (
        <button
          type="button"
          className="mt-1 w-full text-left"
          onClick={() => setExpanded(true)}
        >
          <p className="line-clamp-1 text-sm leading-6 text-fg-secondary">{text}</p>
          <span className={["mt-1 inline-block", BTN_LINK_13].join(" ")}>
            {expandLabel}
          </span>
        </button>
      ) : (
        <>
          <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-fg-secondary">{text}</p>
          <SuccessCriteriaList criteria={criteria} className="mt-2" achieved={isAchieved} />
          {canToggle ? (
            <button
              type="button"
              className={["mt-1", BTN_LINK_13].join(" ")}
              onClick={() => setExpanded(false)}
            >
              Collapse goal
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

function AssistantLoadingDots() {
  return (
    <span className="inline-flex h-6 items-center" aria-label="Assistant is loading">
      <span className="h-3 w-3 animate-pulse rounded-full bg-fg" />
    </span>
  );
}

const STATUS_NOTICE_TONES = {
  "Auto-reply sent": "success",
  "Draft ready": "success",
  "Goal achieved": "success",
  Escalation: "warning",
  "No action required": "neutral",
  "Send failed": "danger",
};

const parseStatusNotice = (text) => {
  const match = text.match(/^\[([^\]]+)\]\s*([\s\S]*)$/);
  if (!match) return null;

  const tone = STATUS_NOTICE_TONES[match[1]];
  if (!tone) return null;

  return { label: match[1], body: match[2].trim(), tone };
};

function AgentMessage() {
  const role = useAuiState((state) => state.message.role);
  const isUser = role === "user";

  return (
    <MessagePrimitive.Root className={["flex w-full", isUser ? "justify-end" : "justify-start"].join(" ")}>
      <div
        className={[
          "text-[15px] leading-7 text-fg",
          isUser
            ? "max-w-[90%] whitespace-pre-wrap break-words rounded-3xl bg-surface-tertiary px-5 py-2.5 sm:max-w-[75%]"
            : "w-full min-w-0 break-words",
        ].join(" ")}
      >
        {isUser ? (
          <>
            <MessagePrimitive.Parts />
            <ErrorPrimitive.Root className="mt-2 text-sm text-danger">
              <ErrorPrimitive.Message />
            </ErrorPrimitive.Root>
          </>
        ) : (
          <AssistantMessageContent />
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

function AssistantStatusNoticeText({ notice }) {
  return (
    <>
      <span className={["mr-1.5 align-middle", statusBadgeClass(notice.tone)].join(" ")}>
        [{notice.label}]
      </span>
      {notice.body}
    </>
  );
}

const ASSISTANT_MESSAGE_PARTS = { Text: AssistantMarkdown };

function AssistantMessageContent() {
  const content = useAuiState((state) => state.message.content ?? state.message.parts);
  const notice = parseStatusNotice(textFromParts(content));

  return (
    <>
      <AuiIf condition={({ message }) => isAssistantMessageLoading(message)}>
        <AssistantLoadingDots />
      </AuiIf>
      <AuiIf condition={({ message }) => !isAssistantMessageLoading(message)}>
        {notice ? (
          <div className="whitespace-pre-wrap">
            <AssistantStatusNoticeText notice={notice} />
          </div>
        ) : (
          <MessagePrimitive.Parts components={ASSISTANT_MESSAGE_PARTS} />
        )}
        <ErrorPrimitive.Root className="mt-2 text-sm text-danger">
          <ErrorPrimitive.Message />
        </ErrorPrimitive.Root>
      </AuiIf>
    </>
  );
}

function DraftReviewContent({
  action,
  conversation,
  subject,
  content,
  emptyLabel,
  isSent,
  canApprove,
  userLanguage = "",
  onDraftTranslated,
  onError,
}) {
  const reviewLanguages = draftReviewLanguages({
    draftLanguage: action?.payload?.language,
    conversationLanguage: action?.payload?.conversation_language,
    fallbackConversationLanguage: userLanguage,
    text: [subject, content].filter((part) => String(part || "").trim()).join("\n\n"),
  });
  const canTranslate = Boolean(conversation?.id && action?.id && content && reviewLanguages);
  const draftLanguage = reviewLanguages?.draftLanguage || "";
  const conversationLanguage = reviewLanguages?.conversationLanguage || "";
  const [showingTranslation, setShowingTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translation, setTranslation] = useState(() => readDraftTranslation(action?.payload));
  const conversationLabel = languageLabel(conversationLanguage);

  const showTranslation = async () => {
    if (!canTranslate || translating) return;
    if (translation?.content) {
      setShowingTranslation(true);
      return;
    }

    setTranslating(true);
    try {
      if (reviewLanguages.persisted) {
        const payload = await fetchJson(
          `/api/agent/conversations/${conversation.id}/actions/${action.id}/translation`,
          { method: "POST" },
        );
        const nextTranslation = readDraftTranslation(payload.action?.payload);
        if (!payload.available || !nextTranslation?.content) {
          onError?.("Couldn't show this draft in the conversation language.");
          return;
        }
        setTranslation(nextTranslation);
        setShowingTranslation(true);
        onDraftTranslated?.(payload.action);
      } else {
        const payload = await fetchJson(`/api/agent/conversations/${conversation.id}/content_translation`, {
          method: "POST",
          body: JSON.stringify({
            text: translationRequestText(content, subject, conversationLanguage),
            target_language: conversationLanguage,
            source_language: draftLanguage,
          }),
        });
        const translated = String(payload.text || "").trim();
        if (!payload.available || !translated) {
          onError?.("Couldn't show this draft in the conversation language.");
          return;
        }
        setTranslation({ content: translated, subject: "" });
        setShowingTranslation(true);
      }
    } catch (e) {
      onError?.(describeError("Couldn't show this draft in the conversation language", e));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <>
      {subject ? (
        <p className="mt-3 font-semibold text-fg">{subject}</p>
      ) : null}
      {content ? (
        <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-fg-secondary">{content}</p>
      ) : (
        <p className="mt-2 text-sm text-fg-tertiary">{emptyLabel}</p>
      )}
      {canTranslate && showingTranslation && translation?.content ? (
        <div id={`draft-translation-${action.id}`} className="mt-3 rounded-xl bg-surface-secondary px-3 py-2.5">
          <p className="text-xs font-medium text-fg-tertiary">{conversationLabel}</p>
          {translation.subject ? (
            <p className="mt-1 font-semibold text-fg">{translation.subject}</p>
          ) : null}
          <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-fg-secondary">{translation.content}</p>
          <p className="mt-2 text-xs leading-5 text-fg-tertiary">
            {translationReviewNote({
              isSent,
              canApprove,
              conversationLanguage,
              draftLanguage,
            })}
          </p>
        </div>
      ) : null}
      {canTranslate ? (
        <button
          type="button"
          className={["mt-2", BTN_LINK_13].join(" ")}
          onClick={() => (showingTranslation ? setShowingTranslation(false) : showTranslation())}
          disabled={translating}
          aria-expanded={showingTranslation}
          aria-controls={`draft-translation-${action.id}`}
        >
          {translating
            ? "Translating..."
            : showingTranslation
              ? `Hide ${conversationLabel}`
              : `Show in ${conversationLabel}`}
        </button>
      ) : null}
    </>
  );
}

function ManualDraftIconActions({ text, onError }) {
  const [copyState, setCopyState] = useState("");
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    setCanShare(typeof navigator.share === "function");
  }, []);

  const copyDraft = async () => {
    if (!text) return;
    try {
      await writeClipboardText(text);
      trackAssistantEvent("manual_draft_copied", { mode: "manual" });
      setCopyState("copied");
      window.setTimeout(() => setCopyState(""), 2000);
    } catch (e) {
      onError?.(describeError("Couldn't copy the draft", e));
    }
  };

  const shareDraft = async () => {
    if (!text || !canShare) return;
    try {
      await navigator.share({ text });
      trackAssistantEvent("manual_draft_shared", { mode: "manual" });
    } catch (e) {
      if (e?.name === "AbortError") return;
      onError?.(describeError("Couldn't share the draft", e));
    }
  };

  return (
    <div className="ml-auto flex shrink-0 items-center gap-1">
      <button
        type="button"
        className={GHOST_ICON_BUTTON_CLASS}
        onClick={copyDraft}
        disabled={!text}
        aria-label={copyState === "copied" ? "Copied" : "Copy reply"}
        title={copyState === "copied" ? "Copied" : "Copy reply"}
      >
        {copyState === "copied" ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
      </button>
      {canShare ? (
        <button
          type="button"
          className={GHOST_ICON_BUTTON_CLASS}
          onClick={shareDraft}
          disabled={!text}
          aria-label="Share"
          title="Share"
        >
          <ShareIcon className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

function ManualTranscriptComposer({ conversation, onAdded, onError }) {
  const [direction, setDirection] = useState("inbound");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const outbound = direction === "outbound";
  const personName = conversation?.recipient?.trim() || "Them";

  const addMessage = async () => {
    const text = body.trim();
    if (!text || saving || conversation?.pending) return;

    setSaving(true);
    try {
      const payload = await fetchJson(`/api/agent/conversations/${conversation.id}/manual_messages`, {
        method: "POST",
        body: JSON.stringify({
          manual_message: {
            body: text,
            direction,
            sender_name: outbound ? "You" : personName,
          },
        }),
      });
      trackAssistantEvent("manual_message_added", { mode: "manual" });
      setBody("");
      onAdded?.(payload.conversation);
    } catch (e) {
      onError?.(describeError("Couldn't add that message", e));
    } finally {
      setSaving(false);
    }
  };

  const paste = async () => {
    try {
      const text = await readClipboardText();
      if (!text.trim()) {
        onError?.("The clipboard is empty.");
        return;
      }
      setBody((current) => (current.trim() ? `${current.replace(/\s+$/, "")}\n${text}` : text));
    } catch (e) {
      onError?.(describeError("Couldn't read the clipboard. Paste with the keyboard instead", e));
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <span className="text-xs font-medium text-fg-secondary">From</span>
        <div className="inline-flex rounded-full bg-surface-tertiary p-0.5" role="group" aria-label="From">
          <button
            type="button"
            className={[segmentClass(!outbound), "max-w-[12rem] truncate"].join(" ")}
            aria-pressed={!outbound}
            title={personName}
            onClick={() => setDirection("inbound")}
          >
            {personName}
          </button>
          <button
            type="button"
            className={segmentClass(outbound)}
            aria-pressed={outbound}
            onClick={() => setDirection("outbound")}
          >
            You
          </button>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs font-medium text-fg-secondary">Message</span>
        <button type="button" className={BTN_LINK_SM} onClick={paste}>
          Paste
        </button>
      </div>
      <textarea
        className={["mt-1 gpt-scroll min-h-16", INPUT_CLASS].join(" ")}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
          event.preventDefault();
          addMessage();
        }}
        placeholder={outbound ? "What you already sent" : "See you Thursday?"}
        aria-label={outbound ? "Message you sent" : "Message"}
      />
      <div className="mt-2 flex justify-end">
        <button type="button" className={BTN_SECONDARY_SM} onClick={addMessage} disabled={saving || !body.trim()}>
          {saving ? "Adding…" : "Add message"}
        </button>
      </div>
    </div>
  );
}

function ConversationDraftCard({
  draft,
  sendApproval,
  sendAction,
  canApprove,
  canCopy = false,
  statusLabel,
  conversation,
  userLanguage = "",
  onActionApproved,
  onDraftTranslated,
  onError,
}) {
  const [approving, setApproving] = useState(false);
  const [locallySent, setLocallySent] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const action = draft || sendApproval || sendAction;
  const approvalAction = sendApproval || draft;
  const messageContent = action?.payload?.body || action?.payload?.text;
  const isManualDraft = isManualDraftAction(draft) || isManualDraftAction(action) || isManualMode(conversation?.mode);
  const isChatDraft = !isManualDraft && (draft?.action_type || sendApproval?.action_type || sendAction?.action_type || "").includes("chat");
  const label = draft
    ? DRAFT_TYPE_LABELS[draft.action_type] || humanizeKey(draft.action_type)
    : isChatDraft
      ? "Draft chat reply"
      : "Draft reply";
  const requiresUserApproval = Boolean(
    (sendApproval || sendAction || draft)?.payload?.requires_user_approval,
  );
  const effectiveStatus = locallySent || statusLabel === "sent" ? "sent" : statusLabel;
  const isSent = effectiveStatus === "sent";
  const canStartApproval = canApprove && !locallySent && Boolean(approvalAction);
  const badgeTone = isSent
    ? "success"
    : effectiveStatus === "awaiting approval" && canStartApproval
      ? "warning"
      : "neutral";
  const sentAt = sendAction?.executed_at || sendAction?.created_at;

  const approveSend = async () => {
    if (!canStartApproval || approving || !conversation) return;

    setApproving(true);
    setConfirmingSend(false);
    try {
      await fetchJson(`/api/agent/conversations/${conversation.id}/actions/${approvalAction.id}/approve`, {
        method: "POST",
      });
      trackAssistantEvent("draft_approved", {
        mode: conversation.mode,
        action_type: approvalAction.action_type,
      });
      setLocallySent(true);
      await onActionApproved?.();
    } catch (e) {
      onError?.(describeError("Couldn't send the approved draft", e));
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="flex w-full justify-start">
      <div className="w-full rounded-2xl border border-line bg-surface px-4 py-3.5 text-sm leading-6 text-fg sm:max-w-[85%]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-surface-tertiary text-fg-secondary">
              {isManualDraft || isChatDraft ? <ChatIcon className="h-3.5 w-3.5" /> : <MailIcon className="h-3.5 w-3.5" />}
            </span>
            <div>
              <p className="text-xs font-medium text-fg">{label}</p>
              <time className="block text-xs text-fg-tertiary">
                {formatTimelineTimestamp(actionTimestamp(action))}
              </time>
            </div>
          </div>
          <span className={statusBadgeClass(badgeTone)}>
            {draftStatusLabel(effectiveStatus)}
          </span>
        </div>
        <DraftReviewContent
          action={action}
          conversation={conversation}
          subject={action?.payload?.subject}
          content={messageContent}
          emptyLabel={isManualDraft || isChatDraft ? "No draft message content." : "No draft email content."}
          isSent={isSent}
          canApprove={canStartApproval}
          userLanguage={userLanguage}
          onDraftTranslated={onDraftTranslated}
          onError={onError}
        />
        {(draft?.error_message || sendApproval?.error_message || sendAction?.error_message) ? (
          <p className="mt-2 text-xs text-danger">
            {draft?.error_message || sendApproval?.error_message || sendAction?.error_message}
          </p>
        ) : null}
        {requiresUserApproval && canStartApproval ? (
          <p className="mt-3 rounded-xl bg-warning-soft px-3 py-2 text-xs leading-5 text-warning">
            Escalated by your guardrails: an agent drafted this reply autonomously but is waiting for your approval.
          </p>
        ) : null}
        <div className="mt-4 flex items-end justify-between gap-2 border-t border-line pt-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
          {isSent ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
              <CheckIcon className="h-3.5 w-3.5" />
              Sent{sentAt && !locallySent ? ` ${formatTimelineTimestamp(sentAt)}` : ""}
            </span>
          ) : confirmingSend && canStartApproval ? (
            <>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                onClick={approveSend}
                disabled={approving}
                aria-label="Confirm approve and send"
              >
                {approving ? "Sending..." : "Confirm send"}
              </button>
              <button
                type="button"
                className={BTN_SECONDARY_SM}
                onClick={() => setConfirmingSend(false)}
                disabled={approving}
                aria-label="Cancel approve and send"
              >
                Cancel
              </button>
            </>
          ) : canStartApproval ? (
            <>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                onClick={() => setConfirmingSend(true)}
                disabled={approving}
              >
                {approving ? "Sending..." : "Approve and send"}
              </button>
              <span className="text-xs text-fg-tertiary">Nothing is sent until you approve.</span>
            </>
          ) : (
            <span className="text-xs text-fg-tertiary">
              {effectiveStatus === "superseded"
                ? "Replaced by a newer draft."
                : effectiveStatus === "failed"
                  ? "Sending failed. Ask the assistant to try again."
                  : "Only the newest draft can be approved."}
            </span>
          )}
          </div>
          {isManualDraft && messageContent ? (
            <ManualDraftIconActions text={messageContent} onError={onError} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Mirrors ConversationDraftCard for goal proposals: the model's goal and
// success criteria are shown for review and only applied to the conversation
// when the user confirms "Approve and set goal". Approving also turns on
// autonomous follow-up, so the confirmation step spells that out.
function ConversationGoalCard({ proposal, canApprove, conversation, onActionApproved, onError }) {
  const [approving, setApproving] = useState(false);
  const [confirmingApproval, setConfirmingApproval] = useState(false);
  const [locallyApproved, setLocallyApproved] = useState(false);
  const goal = proposal?.payload?.goal || "";
  const criteria = successCriteriaList(proposal?.payload?.success_criteria);
  const status = locallyApproved ? "completed" : proposal?.status;
  const isSet = status === "completed";
  const canStartApproval = canApprove && !locallyApproved && proposal?.status === "proposed";
  const badgeTone = isSet ? "success" : canStartApproval ? "warning" : "neutral";
  const setAt = proposal?.executed_at;

  useEffect(() => {
    if (!canStartApproval) setConfirmingApproval(false);
  }, [canStartApproval]);

  const approveGoal = async () => {
    if (!canStartApproval || approving || !conversation) return;

    setApproving(true);
    try {
      await fetchJson(`/api/agent/conversations/${conversation.id}/actions/${proposal.id}/approve`, {
        method: "POST",
      });
      trackAssistantEvent("goal_approved", { mode: conversation.mode });
      setLocallyApproved(true);
      setConfirmingApproval(false);
      await onActionApproved?.();
    } catch (e) {
      onError?.(describeError("Couldn't set the approved goal", e));
    } finally {
      setApproving(false);
    }
  };

  return (
    <div className="flex w-full justify-start">
      <div className="w-full rounded-2xl border border-line bg-surface px-4 py-3.5 text-sm leading-6 text-fg sm:max-w-[85%]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex h-6 w-6 items-center justify-center rounded-md bg-surface-tertiary text-fg-secondary">
              <TargetIcon className="h-3.5 w-3.5" />
            </span>
            <div>
              <p className="text-xs font-medium text-fg">Proposed follow-up goal</p>
              <time className="block text-xs text-fg-tertiary">
                {formatTimelineTimestamp(proposal?.created_at)}
              </time>
            </div>
          </div>
          <span className={statusBadgeClass(badgeTone)}>{goalStatusLabel(status)}</span>
        </div>
        {goal ? (
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-fg">{goal}</p>
        ) : (
          <p className="mt-3 text-sm text-fg-tertiary">No goal text was proposed.</p>
        )}
        <SuccessCriteriaList criteria={criteria} className="mt-3" />
        {proposal?.error_message && !isSet ? (
          <p className="mt-2 text-xs text-fg-tertiary">{proposal.error_message}</p>
        ) : null}
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          {isSet ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-success">
              <CheckIcon className="h-3.5 w-3.5" />
              Goal set and autonomous follow-up turned on{setAt && !locallyApproved ? ` ${formatTimelineTimestamp(setAt)}` : ""}
            </span>
          ) : confirmingApproval ? (
            <>
              <p className="w-full text-sm leading-6 text-fg">
                Set this goal and turn on autonomous follow-up? The assistant will reply on your behalf within your rules until you turn it off.
              </p>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                onClick={approveGoal}
                disabled={approving}
                aria-label="Confirm approve and set goal"
              >
                {approving ? "Setting goal..." : "Confirm and turn on follow-up"}
              </button>
              <button
                type="button"
                className={BTN_SECONDARY_SM}
                onClick={() => setConfirmingApproval(false)}
                disabled={approving}
                aria-label="Cancel approve and set goal"
              >
                Cancel
              </button>
            </>
          ) : canStartApproval ? (
            <>
              <button
                type="button"
                className={BTN_PRIMARY_SM}
                onClick={() => setConfirmingApproval(true)}
                disabled={approving}
              >
                Approve and set goal
              </button>
              <span className="text-xs text-fg-tertiary">Approving also turns on autonomous follow-up.</span>
            </>
          ) : (
            <span className="text-xs text-fg-tertiary">
              {status === "blocked"
                ? "Replaced by a newer goal proposal."
                : status === "failed"
                  ? "Setting the goal failed. Ask the assistant to propose it again."
                  : "Only the newest goal proposal can be approved."}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function isAssistantMessageLoading(message) {
  const status = message.status;
  if (status === "running") return true;
  if (status?.type === "running") return true;
  return false;
}

const OPEN_CONVERSATION_EXPLAIN_PROMPTS = {
  "Explain this email": {
    label: "Explain latest email",
    prompt: "Explain latest email. What is it about, what are they asking, and what should I know before I reply?",
  },
  "Explain this message": {
    label: "Explain latest message",
    prompt: "Explain latest message. What is it about, what are they asking, and what should I know before I reply?",
  },
};

const OPEN_CONVERSATION_SUGGEST_PROMPTS = {
  reply_email: "Suggest a reply to latest email, and explain why this reply is a good response.",
  reply_chat: "Suggest a reply to latest message, and explain why this reply is a good response.",
};

function composerExamplePrompts(mode, showGoalSuggestion) {
  const suggestPrompt = OPEN_CONVERSATION_SUGGEST_PROMPTS[mode];
  const modePrompts = (SETUP_EXAMPLE_PROMPTS[mode] ?? []).map((example) => {
    const opened = OPEN_CONVERSATION_EXPLAIN_PROMPTS[example.label] ?? example;
    if (example.label === "Suggest a reply" && suggestPrompt) {
      return { ...opened, prompt: suggestPrompt };
    }
    return opened;
  });
  const prompts = modePrompts.length
    ? modePrompts
    : [{ label: SET_FOLLOW_UP_GOAL_LABEL, prompt: SET_FOLLOW_UP_GOAL_PROMPT }];
  if (showGoalSuggestion) return prompts;
  return prompts.filter((example) => example.label !== SET_FOLLOW_UP_GOAL_LABEL);
}

function AgentComposer({ mode, showGoalSuggestion = true, placeholder }) {
  const prompts = composerExamplePrompts(mode, showGoalSuggestion);

  return (
    <ComposerPrimitive.Root className={[COMPOSER_SHELL_CLASS, "p-2.5"].join(" ")}>
      <ComposerPrimitive.Input
        className="gpt-scroll max-h-52 w-full resize-none bg-transparent px-3 py-2 text-base leading-6 text-fg outline-none placeholder:text-fg-tertiary"
        placeholder={placeholder || "Give the assistant guidance, ask for a draft, or tell it to continue..."}
        rows={1}
        autoFocus
      />
      <div className="flex items-center justify-between gap-2 px-1 pb-0.5">
        <div className="flex min-w-0 flex-1 flex-wrap gap-2">
          {prompts.length ? (
            prompts.map((example) => (
              <ThreadPrimitive.Suggestion
                key={example.label}
                prompt={example.prompt}
                className={chipClass(false)}
              >
                {example.label}
              </ThreadPrimitive.Suggestion>
            ))
          ) : (
            <span className="block truncate px-1 text-xs text-fg-tertiary">Drafts stay here until you copy them.</span>
          )}
        </div>
        <AuiIf condition={(state) => !state.thread.isRunning}>
          <ComposerPrimitive.Send className={ICON_BUTTON_CLASS} aria-label="Send message" title="Send">
            <ArrowUpIcon className="h-4 w-4" />
          </ComposerPrimitive.Send>
        </AuiIf>
        <AuiIf condition={(state) => state.thread.isRunning}>
          <ComposerPrimitive.Cancel className={ICON_BUTTON_CLASS} aria-label="Stop generating" title="Stop">
            <StopIcon />
          </ComposerPrimitive.Cancel>
        </AuiIf>
      </div>
    </ComposerPrimitive.Root>
  );
}

const DEFAULT_GUARDRAILS =
  "Stay within the conversation goal and escalate if the topic or tone changes. Do not escalate only because the recipient changes. Escalate when disclosing private, sensitive, or confidential information is not clearly authorized.";

const EXPIRY_OPTIONS = [
  { value: "", label: "No expiry" },
  { value: "1", label: "1 hour" },
  { value: "4", label: "4 hours" },
  { value: "24", label: "1 day" },
  { value: "72", label: "3 days" },
  { value: "168", label: "1 week" },
];

const SILENCE_WAIT_OPTIONS = [
  { value: "4", label: "4 hours" },
  { value: "24", label: "1 day" },
  { value: "48", label: "2 days" },
  { value: "72", label: "3 days" },
  { value: "168", label: "1 week" },
];

const SILENCE_REPEAT_OPTIONS = [
  { value: "1", label: "Once" },
  { value: "2", label: "Up to 2 times" },
  { value: "3", label: "Up to 3 times" },
];

function authorizationRequestBody(settings) {
  const authorization = {
    max_sends: Number(settings.maxSends) || 0,
    guardrails: settings.guardrails.trim(),
    require_approval: settings.requireApproval,
    silence_follow_up: Boolean(settings.silenceFollowUp),
    silence_follow_up_after_hours: Number(settings.silenceFollowUpAfterHours) || 48,
    silence_follow_up_max: Number(settings.silenceFollowUpMax) || 1,
  };
  if (settings.expiresInHours) {
    authorization.expires_at = new Date(
      Date.now() + Number(settings.expiresInHours) * 60 * 60 * 1000,
    ).toISOString();
  }
  return authorization;
}

function silenceWaitLabel(hours) {
  return SILENCE_WAIT_OPTIONS.find((option) => option.value === String(hours))?.label || "2 days";
}

function AuthorizationToggle({ conversation, settings, onChanged, onError, compact = false }) {
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const isAuthorized = conversation.authorization_state === "authorized";
  const canEnable = Boolean(conversation.goal);
  const stateDescription = !canEnable
    ? "Set a follow-up goal in chat to enable."
    : isAuthorized
      ? "Authorized to follow up within your rules."
      : "Off until you enable authorization.";

  useEffect(() => {
    setConfirming(false);
  }, [conversation.id, conversation.authorization_state]);

  const applyToggle = async () => {
    if (saving || (!isAuthorized && !canEnable)) return;

    setSaving(true);
    setConfirming(false);
    try {
      if (isAuthorized) {
        await fetchJson(`/api/agent/conversations/${conversation.id}/authorization`, { method: "DELETE" });
      } else {
        await fetchJson(`/api/agent/conversations/${conversation.id}/authorization`, {
          method: "POST",
          body: JSON.stringify({ authorization: authorizationRequestBody(settings) }),
        });
      }
      trackAssistantEvent(
        isAuthorized ? "autonomous_replies_disabled" : "autonomous_replies_enabled",
        authorizationAnalyticsParams(conversation.mode, settings),
      );
      onChanged?.();
    } catch (e) {
      onError?.(describeError(isAuthorized ? "Couldn't turn off autonomous replies" : "Couldn't turn on autonomous replies", e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={[
        "flex items-center justify-between gap-3",
        compact ? "" : "mt-3 rounded-xl border border-line bg-surface-secondary px-3 py-2.5",
      ].join(" ")}
    >
      <div className="min-w-0">
        <p className="text-[13px]/4 font-medium text-fg">
          Autonomous replies
          <span className={["ml-1.5 font-normal", isAuthorized ? "text-success" : "text-fg-tertiary"].join(" ")}>
            {isAuthorized ? "On" : "Off"}
          </span>
        </p>
        <p className={["mt-0.5 text-[13px] leading-5 text-fg-secondary", compact ? "sr-only" : ""].join(" ")}>
          {stateDescription}
        </p>
      </div>
      {confirming ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            className={[
              "rounded-md px-2 py-1 text-[13px]/4 font-medium transition disabled:cursor-not-allowed disabled:opacity-50",
              isAuthorized
                ? "text-danger hover:bg-danger-soft"
                : "text-fg hover:bg-surface-active",
            ].join(" ")}
            aria-label={isAuthorized ? "Confirm turning off autonomous replies" : "Confirm turning on autonomous replies"}
            disabled={saving}
            onClick={applyToggle}
          >
            {saving ? "Saving…" : isAuthorized ? "Turn off" : "Turn on"}
          </button>
          <button
            type="button"
            className="rounded-md p-1 text-fg-tertiary transition hover:bg-surface-active hover:text-fg disabled:cursor-not-allowed disabled:opacity-50"
            aria-label="Cancel"
            disabled={saving}
            onClick={() => setConfirming(false)}
          >
            <CloseIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={isAuthorized}
          aria-label={isAuthorized ? "Turn off autonomous replies" : "Turn on autonomous replies"}
          title={stateDescription}
          disabled={saving || (!isAuthorized && !canEnable)}
          onClick={() => setConfirming(true)}
          className={[
            "relative h-6 w-11 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40",
            isAuthorized ? "bg-success" : "bg-line-strong",
          ].join(" ")}
        >
          <span
            className={[
              "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition",
              isAuthorized ? "translate-x-5" : "translate-x-0",
            ].join(" ")}
          />
        </button>
      )}
    </div>
  );
}

function silenceFollowUpStatus(conversation, settings) {
  const wait = silenceWaitLabel(settings.silenceFollowUpAfterHours);
  const max = Number(settings.silenceFollowUpMax) || 1;
  const cadence = max > 1 ? `Up to ${max} follow-ups` : "One follow-up";
  const manual = conversation.mode === "manual";
  const delivery = manual
    ? "PromptMail prepares a draft for you to copy and does not send it."
    : "Each follow-up counts toward max sends.";

  if (!settings.silenceFollowUp) {
    return `Off. A message you send will not be followed up if they stay silent. ${delivery}`;
  }

  if (conversation.authorization_state !== "authorized") {
    return `${cadence} if there is no reply for ${wait}. Starts once autonomous replies are on. ${delivery}`;
  }

  if (conversation.silence_follow_up_held) {
    return `Paused for the last message. Turn this off and on to allow another try, or send a new message. ${delivery}`;
  }

  if (conversation.awaiting_reply_since) {
    const sent = Number(conversation.silence_follow_ups_sent) || 0;
    const waitingSince = formatTimelineTimestamp(conversation.awaiting_reply_since);
    if (sent >= max) {
      return `Waiting since ${waitingSince}. The follow-up limit for this message is reached. ${delivery}`;
    }
    const nextAt = new Date(Date.parse(conversation.awaiting_reply_since) + Number(settings.silenceFollowUpAfterHours) * 60 * 60 * 1000);
    if (nextAt.getTime() <= Date.now()) {
      const ready = manual
        ? "A follow-up is due and will be drafted on the next check."
        : "A follow-up is due and will be sent on the next check.";
      return `Waiting since ${waitingSince}. ${ready} (${sent} of ${max} sent). ${delivery}`;
    }
    return `Waiting since ${waitingSince}. Next follow-up ${formatTimelineTimestamp(nextAt.toISOString())} (${sent} of ${max} sent). ${delivery}`;
  }

  const starts = manual
    ? "The timer starts on the next message you send."
    : "If your last message in Gmail or Chat has no reply, the wait counts from when you sent it. Otherwise it starts on the next message you send.";
  return `${cadence} if there is no reply for ${wait}. ${starts} ${delivery}`;
}

function SilenceFollowUpControls({ conversation, settings, onSettingsChange, onChanged, onError }) {
  const [saving, setSaving] = useState(false);
  const enabled = Boolean(settings.silenceFollowUp);
  const saveQueue = useRef(Promise.resolve());
  const latestSettings = useRef(settings);
  latestSettings.current = settings;

  const persist = (next, eventName) => {
    const previous = settings;
    onSettingsChange(next);
    latestSettings.current = next;
    const snapshot = next;
    const run = saveQueue.current.catch(() => {}).then(async () => {
      setSaving(true);
      try {
        await fetchJson(`/api/agent/conversations/${conversation.id}/silence_follow_up`, {
          method: "PATCH",
          body: JSON.stringify({
            silence_follow_up: {
              enabled: Boolean(snapshot.silenceFollowUp),
              after_hours: Number(snapshot.silenceFollowUpAfterHours) || 48,
              max_follow_ups: Number(snapshot.silenceFollowUpMax) || 1,
            },
          }),
        });
        if (latestSettings.current !== snapshot) return;

        if (eventName) trackAssistantEvent(eventName, authorizationAnalyticsParams(conversation.mode, snapshot));
        onChanged?.();
      } catch (e) {
        if (latestSettings.current === snapshot) {
          latestSettings.current = previous;
          onSettingsChange(previous);
        }
        onError?.(describeError("Couldn't update silence follow-up", e));
      } finally {
        if (latestSettings.current === snapshot) setSaving(false);
      }
    });
    saveQueue.current = run;
    return run;
  };

  const intro = conversation.mode === "manual"
    ? "If they don’t reply to your last message, whether you approved it or it was sent automatically."
    : "If they don’t reply to your last message, including one you already sent in Gmail or Chat, the wait counts from when you sent it.";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-line px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-fg">
              Follow up on silence
              <span className={["ml-1.5 font-normal", enabled ? "text-success" : "text-fg-tertiary"].join(" ")}>
                {enabled ? "On" : "Off"}
              </span>
            </p>
            <p className="mt-1 text-[13px] leading-5 text-fg-secondary">
              {intro}
              {settings.requireApproval ? " You’ll approve each follow-up before it is sent." : ""}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            aria-label={enabled ? "Turn off silence follow-up" : "Turn on silence follow-up"}
            disabled={saving}
            onClick={() => persist(
              { ...settings, silenceFollowUp: !enabled },
              enabled ? "silence_follow_up_disabled" : "silence_follow_up_enabled",
            )}
            className={[
              "relative mt-0.5 h-6 w-11 shrink-0 rounded-full transition disabled:cursor-not-allowed disabled:opacity-40",
              enabled ? "bg-success" : "bg-line-strong",
            ].join(" ")}
          >
            <span
              className={[
                "absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition",
                enabled ? "translate-x-5" : "translate-x-0",
              ].join(" ")}
            />
          </button>
        </div>
      </div>
      <div className="gpt-scroll min-h-0 flex-1 overflow-y-auto p-4">
        <section className={CARD_CLASS}>
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-[13px]/4 font-medium text-fg-secondary" htmlFor={`silence-wait-${conversation.id}`}>
                Wait
              </label>
              <select
                id={`silence-wait-${conversation.id}`}
                className={["mt-1", INPUT_CLASS.replace("w-full ", "")].join(" ")}
                value={String(settings.silenceFollowUpAfterHours || "48")}
                disabled={saving}
                onChange={(event) => persist({ ...settings, silenceFollowUpAfterHours: event.target.value })}
              >
                {SILENCE_WAIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[13px]/4 font-medium text-fg-secondary" htmlFor={`silence-repeat-${conversation.id}`}>
                Repeat
              </label>
              <select
                id={`silence-repeat-${conversation.id}`}
                className={["mt-1", INPUT_CLASS.replace("w-full ", "")].join(" ")}
                value={String(settings.silenceFollowUpMax || "1")}
                disabled={saving}
                onChange={(event) => persist({ ...settings, silenceFollowUpMax: event.target.value })}
              >
                {SILENCE_REPEAT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="mt-4 text-[13px] leading-5 text-fg-secondary">
            {saving ? "Saving…" : silenceFollowUpStatus(conversation, settings)}
          </p>
        </section>
      </div>
    </div>
  );
}

function ConversationGuardrailsSection({
  conversation,
  settings,
  onSettingsChange,
  onChanged,
  onError,
}) {
  const [saving, setSaving] = useState(false);
  const isAuthorized = conversation.authorization_state === "authorized";
  const limits = conversation.authorization_limits ?? {};

  const applySettings = async () => {
    if (!isAuthorized || saving) return;

    setSaving(true);
    try {
      await fetchJson(`/api/agent/conversations/${conversation.id}/authorization`, {
        method: "POST",
        body: JSON.stringify({ authorization: authorizationRequestBody(settings) }),
      });
      trackAssistantEvent("authorization_updated", authorizationAnalyticsParams(conversation.mode, settings));
      onChanged?.();
    } catch (e) {
      onError?.(describeError("Couldn't update the authorization settings", e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={CARD_CLASS}>
      <p className="text-sm font-semibold text-fg">Conversation guardrails</p>
      <p className="mt-1 text-sm leading-6 text-fg-secondary">
        Limits for this conversation’s autonomous follow-up. Used when you enable authorization.
      </p>

      {isAuthorized && (
        <div className="mt-3 space-y-1 rounded-xl bg-surface-secondary p-3 text-[13px]/4 text-fg-secondary">
          <p className="flex items-center gap-1.5 font-medium text-fg">
            <span className="h-1.5 w-1.5 rounded-full bg-success" />
            Currently active
          </p>
          {limits.guardrails && <p className="leading-5">{limits.guardrails}</p>}
          <p>
            {Number(limits.max_sends) > 0 ? `Max sends: ${limits.max_sends}` : "Max sends: unlimited"}
            {limits.expires_at ? ` · Expires: ${new Date(limits.expires_at).toLocaleString()}` : ""}
          </p>
          {Boolean(limits.require_approval) && (
            <p className="font-medium text-warning">
              Every send is escalated to you for approval before delivery.
            </p>
          )}
          {Boolean(limits.silence_follow_up) && (
            <p>
              {`Follows up on silence after ${silenceWaitLabel(limits.silence_follow_up_after_hours)}, ${Number(limits.silence_follow_up_max) > 1 ? `up to ${limits.silence_follow_up_max} times` : "once"}.`}
            </p>
          )}
        </div>
      )}

      <div className="mt-4 space-y-4">
        <div>
          <label
            className="block text-[13px]/4 font-medium text-fg-secondary"
            htmlFor={`guardrails-${conversation.id}`}
          >
            Guardrails
          </label>
          <textarea
            id={`guardrails-${conversation.id}`}
            className={["mt-1 resize-none", INPUT_CLASS].join(" ")}
            rows={3}
            value={settings.guardrails}
            onChange={(event) => onSettingsChange({ ...settings, guardrails: event.target.value })}
            placeholder="Rules an agent must follow. When a rule would be crossed, it escalates to you instead of acting."
          />
          <p className="mt-1 text-[13px]/4 text-fg-tertiary">
            An agent escalates to you instead of replying whenever a guardrail would be crossed.
          </p>
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <label
              className="block text-[13px]/4 font-medium text-fg-secondary"
              htmlFor={`max-sends-${conversation.id}`}
            >
              Max sends
            </label>
            <input
              id={`max-sends-${conversation.id}`}
              type="number"
              min="0"
              className={["mt-1 w-24", INPUT_CLASS.replace("w-full ", "")].join(" ")}
              value={settings.maxSends}
              onChange={(event) => onSettingsChange({ ...settings, maxSends: event.target.value })}
            />
            <p className="mt-1 text-[13px]/4 text-fg-tertiary">
              Hard limit enforced by the app; an agent escalates once reached.
            </p>
          </div>
          <div>
            <label
              className="block text-[13px]/4 font-medium text-fg-secondary"
              htmlFor={`expires-${conversation.id}`}
            >
              Expires
            </label>
            <select
              id={`expires-${conversation.id}`}
              className={["mt-1", INPUT_CLASS.replace("w-full ", "")].join(" ")}
              value={settings.expiresInHours}
              onChange={(event) => onSettingsChange({ ...settings, expiresInHours: event.target.value })}
            >
              {EXPIRY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        <label className="flex items-start gap-2.5 text-sm text-fg">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-line-strong accent-fg"
            checked={settings.requireApproval}
            onChange={(event) => onSettingsChange({ ...settings, requireApproval: event.target.checked })}
          />
          <span>
            Always escalate to me before sending
            <span className="block text-[13px]/4 text-fg-tertiary">
              An agent drafts replies but never sends them until you approve each one.
            </span>
          </span>
        </label>
        {isAuthorized ? (
          <button
            type="button"
            className={BTN_PRIMARY_SM.replace("text-xs", "text-[13px]/4")}
            onClick={applySettings}
            disabled={saving}
          >
            {saving ? "Updating…" : "Update authorization"}
          </button>
        ) : (
          <p className="text-[13px]/4 text-fg-tertiary">
            Enable autonomous replies above to authorize with these settings.
          </p>
        )}
      </div>
    </section>
  );
}

const timestampValue = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

const chatSendersMatch = (left, right) => {
  if (!left || !right) return false;
  if (left.email && right.email) return left.email.toLowerCase() === right.email.toLowerCase();
  if (left.name && right.name) return left.name === right.name;
  return false;
};

function manualMessagesForTimeline(conversation) {
  const messages = Array.isArray(conversation?.manual_messages) ? conversation.manual_messages : [];
  return messages.filter((message) => message?.body);
}

function chatMessagesForTimeline(conversation, chatMessages = []) {
  const selectedChat = conversation.selected_chat_snapshot;
  const threadName = conversation.selected_thread_name;
  const selectedTime = timestampValue(selectedChat?.create_time);
  const partnerSender = selectedChat?.sender;
  const byName = new Map();

  const addMessage = (message) => {
    if (message?.name) byName.set(message.name, message);
  };

  if (selectedChat) addMessage(selectedChat);

  chatMessages.forEach((message) => {
    if (!threadName || message.thread_name === threadName) {
      addMessage(message);
      return;
    }

    if (
      partnerSender &&
      chatSendersMatch(message.sender, partnerSender) &&
      timestampValue(message.create_time) >= selectedTime
    ) {
      addMessage(message);
    }
  });

  return [...byName.values()];
}

const NO_GOAL_LABEL = "No follow-up goal set yet. Set one in chat to enable autonomous follow-up.";

const formatTimelineTimestamp = (value) => {
  if (!value || Number.isNaN(Date.parse(value))) return "Time unavailable";

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
};

const formatInboxListTimestamp = (value) => {
  if (value == null || value === "") return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();

  return new Intl.DateTimeFormat(
    undefined,
    isToday ? { timeStyle: "short" } : { dateStyle: "medium" }
  ).format(date);
};

const formatTimeAgo = (value, now = Date.now()) => {
  if (value == null || value === "") return null;

  const date = value instanceof Date ? value : new Date(value);
  const then = date.getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.max(0, Math.round((now - then) / 1000));
  if (seconds < 45) return "just now";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"} ago`;

  return formatInboxListTimestamp(value);
};

const EMAIL_FRAGMENT_IFRAME_STYLES = `
  body { margin: 0; padding: 12px; word-break: break-word; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; }
`;

const buildEmailIframeDocument = (html) => {
  const trimmed = html.trim();
  const isFullDocument = /^\s*<!doctype\s+html/i.test(trimmed) || /^\s*<html[\s>]/i.test(trimmed);

  if (isFullDocument) {
    if (/<base[\s>]/i.test(trimmed)) return trimmed;
    if (/<head[\s>]/i.test(trimmed)) {
      return trimmed.replace(/<head([^>]*)>/i, '<head$1><base target="_blank" rel="noopener noreferrer">');
    }
    return trimmed.replace(/<html([^>]*)>/i, '<html$1><head><base target="_blank" rel="noopener noreferrer"></head>');
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank" rel="noopener noreferrer"><style>${EMAIL_FRAGMENT_IFRAME_STYLES}</style></head><body>${trimmed}</body></html>`;
};

function EmailHtmlBody({ html, plainText }) {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !html?.trim()) return undefined;

    const doc = iframe.contentDocument;
    if (!doc) return undefined;

    doc.open();
    doc.write(buildEmailIframeDocument(html));
    doc.close();

    const resize = () => {
      const height = Math.max(doc.body?.scrollHeight ?? 0, doc.documentElement?.scrollHeight ?? 0);
      iframe.style.height = `${height}px`;
    };

    resize();
    const resizeObserver = typeof ResizeObserver !== "undefined" && doc.body
      ? new ResizeObserver(resize)
      : null;
    resizeObserver?.observe(doc.body);
    window.addEventListener("resize", resize);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [html]);

  if (!html?.trim()) {
    return (
      <p className="mt-3 whitespace-pre-wrap rounded-lg bg-surface p-3 text-[14px] leading-5 text-fg-secondary">
        {plainText || "No email body available."}
      </p>
    );
  }

  return (
    <iframe
      ref={iframeRef}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      className="mt-3 w-full rounded-lg border border-line bg-white"
      title="Email body"
      style={{ minHeight: "4rem" }}
    />
  );
}

const actionTimestamp = (action) => action.executed_at || action.created_at;

const isDraftAction = (action) => action.action_type?.startsWith("draft_");

const isSendAction = (action) => action.action_type?.startsWith("send_");

const isProposedSendAction = (action) =>
  isSendAction(action) && action.status === "proposed";

const isSupersededSendAction = (action) =>
  isSendAction(action) && action.status === "blocked";

const isEscalationAction = (action) => action.action_type === "escalate_for_approval";

const isGoalAction = (action) => action.action_type === "set_goal";

const isGoalAchievedAction = (action) => action.action_type === "goal_achieved";

// Goal proposals live in the chat feed as approval cards; only an approved
// (completed) goal is also recorded in the timeline.
const isPendingGoalProposal = (action) =>
  isGoalAction(action) && action.status !== "completed";

// Goal proposals render as approval cards in the chat feed; a goal-completion
// report is only the [Goal achieved] notice plus a timeline event.
const buildConversationGoalItems = (actions = []) => {
  const proposals = [...actions]
    .filter(isGoalAction)
    .sort((left, right) => chronologicalValue(left.created_at) - chronologicalValue(right.created_at));
  const newestPending = [...proposals].reverse().find((proposal) => proposal.status === "proposed");

  return proposals.map((proposal) => ({
    id: `goal-${proposal.id}`,
    proposal,
    timestamp: proposal.created_at,
    canApprove: Boolean(newestPending) && proposal.id === newestPending.id,
  }));
};

const actionMessageContent = (action) =>
  (action?.payload?.body || action?.payload?.text || "").trim();

const draftMatchesSend = (draft, send) => {
  const draftContent = actionMessageContent(draft);
  const sendContent = actionMessageContent(send);
  if (!draftContent || !sendContent || draftContent !== sendContent) return false;

  if (draft.payload?.subject || send.payload?.subject) {
    return (draft.payload?.subject || "") === (send.payload?.subject || "");
  }

  return true;
};

const pickMatchingSend = (draft, sends, usedSendIds) => {
  const matches = sends.filter(
    (send) =>
      !usedSendIds.has(send.id) &&
      draftMatchesSend(draft, send) &&
      chronologicalValue(actionTimestamp(send)) >= chronologicalValue(actionTimestamp(draft)),
  );

  return (
    matches.find((send) => send.status === "proposed")
    || matches.find((send) => send.status === "completed")
    || matches[0]
    || null
  );
};

const draftItemStatus = (sendAction) => {
  if (sendAction?.status === "completed") return "sent";
  if (sendAction?.status === "blocked") return "superseded";
  if (sendAction?.status === "failed") return "failed";
  if (sendAction?.status === "proposed") return "awaiting approval";
  return "awaiting approval";
};

const buildConversationDraftItems = (actions = []) => {
  const drafts = [...actions]
    .filter(isDraftAction)
    .sort(
      (left, right) =>
        chronologicalValue(actionTimestamp(left)) - chronologicalValue(actionTimestamp(right)),
    );
  const sends = [...actions]
    .filter(isSendAction)
    .sort(
      (left, right) =>
        chronologicalValue(actionTimestamp(left)) - chronologicalValue(actionTimestamp(right)),
    );

  const usedSendIds = new Set();
  const items = drafts.map((draft) => {
    const sendAction = pickMatchingSend(draft, sends, usedSendIds);
    if (sendAction) usedSendIds.add(sendAction.id);

    return {
      id: `draft-${draft.id}`,
      draft,
      sendAction,
      sendApproval: sendAction?.status === "proposed" ? sendAction : null,
      timestamp: actionTimestamp(draft),
      statusLabel: draftItemStatus(sendAction),
    };
  });

  sends.forEach((send) => {
    if (usedSendIds.has(send.id)) return;
    items.push({
      id: `send-${send.id}`,
      draft: null,
      sendAction: send,
      sendApproval: send.status === "proposed" ? send : null,
      timestamp: actionTimestamp(send),
      statusLabel: draftItemStatus(send),
    });
  });

  const sortedItems = items.sort(
    (left, right) => chronologicalValue(left.timestamp) - chronologicalValue(right.timestamp),
  );

  const sendItems = sortedItems.filter((item) => !isManualDraftAction(item.draft));
  const newest = sendItems[sendItems.length - 1];
  const newestApprovableId =
    newest
    && newest.sendAction?.status !== "completed"
    && (newest.sendAction?.status === "proposed" || (!newest.sendAction && newest.draft))
      ? newest.id
      : null;
  const manualItems = sortedItems.filter((item) => isManualDraftAction(item.draft));
  const newestManualId = manualItems[manualItems.length - 1]?.id ?? null;

  return sortedItems.map((item) => {
    if (!isManualDraftAction(item.draft)) {
      return { ...item, canApprove: item.id === newestApprovableId, canCopy: false };
    }

    const isNewest = item.id === newestManualId;
    const sent = item.draft.status === "completed";
    const body = item.draft?.payload?.body || item.draft?.payload?.text;
    return {
      ...item,
      sendAction: null,
      sendApproval: null,
      canApprove: isNewest && !sent && item.draft.status === "proposed" && Boolean(body),
      canCopy: false,
      statusLabel: sent ? "sent" : (isNewest ? "awaiting approval" : "superseded"),
    };
  });
};

const CONTENT_PREVIEW_MAX_CHARS = 240;

const contentNeedsCollapse = (content) =>
  content.length > CONTENT_PREVIEW_MAX_CHARS || content.split("\n").length > 4;

function buildTimelineEntries(conversation, actions, threadEmails = [], chatMessages = []) {
  if (!conversation) return [];

  const entries = [];
  const conversationTimestamp = conversation.created_at || conversation.updated_at;

  if (isManualMode(conversation.mode)) {
    manualMessagesForTimeline(conversation).forEach((message) => {
      entries.push({
        id: `manual-${message.id}`,
        type: "manual",
        timestamp: message.added_at || conversationTimestamp,
        sortValue: timestampValue(message.added_at || conversationTimestamp),
        manualMessage: message,
      });
    });
  } else if (isChatMode(conversation.mode)) {
    const selectedChatMessageName = conversation.selected_chat_message_name || conversation.selected_chat_snapshot?.name;
    const messages = chatMessagesForTimeline(conversation, chatMessages);

    messages.forEach((message) => {
      entries.push({
        id: `chat-${message.name}`,
        type: "chat",
        timestamp: message.create_time || conversationTimestamp,
        sortValue: timestampValue(message.create_time || conversationTimestamp),
        chatMessage: message,
        isSelected: message.name === selectedChatMessageName,
      });
    });
  } else {
    const selectedEmail = conversation.selected_email_snapshot;
    const selectedMessageId = conversation.selected_message_id || selectedEmail?.id;

    if (conversationShowsThreadEmails(conversation)) {
      const emails = threadEmails.length > 0
        ? threadEmails
        : (selectedEmail ? [selectedEmail] : []);

      emails.forEach((email) => {
        entries.push({
          id: `email-${email.id}`,
          type: "email",
          timestamp: email.date || conversationTimestamp,
          sortValue: timestampValue(email.date || conversationTimestamp),
          email,
          isSelected: email.id === selectedMessageId,
        });
      });
    } else {
      entries.push({
        id: selectedEmail?.id ? `email-${selectedEmail.id}` : `conversation-${conversation.id}`,
        type: "email",
        timestamp: conversationTimestamp,
        sortValue: timestampValue(conversationTimestamp),
        email: selectedEmail,
        isSelected: true,
      });
    }
  }

  actions.forEach((action) => {
    if (
      isDraftAction(action)
      || isProposedSendAction(action)
      || isSupersededSendAction(action)
      || isEscalationAction(action)
      || isPendingGoalProposal(action)
    ) return;

    const timestamp = actionTimestamp(action);
    entries.push({
      id: `action-${action.id}`,
      type: "action",
      timestamp,
      sortValue: timestampValue(timestamp),
      action,
    });
  });

  // Timestamps have second precision, so actions recorded together (goal set,
  // then autonomous replies turned on) fall back to creation order.
  return entries.sort((left, right) => {
    if (left.sortValue !== right.sortValue) return left.sortValue - right.sortValue;
    return (left.action?.id ?? 0) - (right.action?.id ?? 0);
  });
}

function SilenceFollowUpPane({ conversation, settings, onSettingsChange, onChanged, onError }) {
  if (!conversation) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="max-w-xs text-sm leading-6 text-fg-tertiary">
          Select a conversation to follow up when they stay silent.
        </p>
      </div>
    );
  }

  return (
    <SilenceFollowUpControls
      conversation={conversation}
      settings={settings}
      onSettingsChange={onSettingsChange}
      onChanged={onChanged}
      onError={onError}
    />
  );
}

function RulesPane({
  conversation,
  authSettings,
  onAuthSettingsChange,
  autonomousRules,
  onAuthorizationChanged,
  onSaved,
  onError,
}) {
  const [markdown, setMarkdown] = useState(autonomousRules?.markdown ?? "");
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState("");

  useEffect(() => {
    if (!autonomousRules) return;
    setMarkdown(autonomousRules.markdown);
  }, [autonomousRules]);

  const dirty = Boolean(autonomousRules) && markdown !== autonomousRules.markdown;

  const save = async () => {
    setSaving(true);
    try {
      const payload = await fetchJson("/api/agent/autonomous_rules", {
        method: "PATCH",
        body: JSON.stringify({ markdown }),
      });
      onSaved(payload);
      setMarkdown(payload.markdown);
      setSaveState("saved");
      trackAssistantEvent("standing_rules_saved");
    } catch (e) {
      onError(describeError("Couldn't save your rules", e));
      setSaveState("error");
    } finally {
      setSaving(false);
    }
  };

  if (!autonomousRules) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-sm text-fg-tertiary">
        Loading your rules…
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="border-b border-line px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-fg">
              Standing rules
              <span className="ml-1.5 font-mono text-[13px]/4 font-normal text-fg-tertiary">AGENT.md</span>
            </p>
            <p className="mt-1 text-[13px] leading-5 text-fg-secondary">
              Instructions an agent follows in every autonomous email and Google Chat conversation.
              Send limits and expiry are set per conversation below.
            </p>
          </div>
          <button
            type="button"
            className={BTN_PRIMARY_SM.replace("text-xs", "text-[13px]/4")}
            onClick={save}
            disabled={saving || !dirty}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        {saveState === "saved" && !dirty && (
          <p className="mt-2 text-[13px]/4 text-success">Rules saved. They apply to the next autonomous run.</p>
        )}
      </div>

      <div className="gpt-scroll min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {conversation && authSettings ? (
          <ConversationGuardrailsSection
            conversation={conversation}
            settings={authSettings}
            onSettingsChange={onAuthSettingsChange}
            onChanged={onAuthorizationChanged}
            onError={onError}
          />
        ) : null}

        <section className={["flex min-h-[16rem] flex-col", CARD_CLASS].join(" ")}>
          <p className="text-sm font-semibold text-fg">Rules file</p>
          <textarea
            value={markdown}
            onChange={(event) => {
              setMarkdown(event.target.value);
              setSaveState("");
            }}
            spellCheck={false}
            className="gpt-scroll mt-3 min-h-[16rem] flex-1 resize-y rounded-xl border border-line bg-surface-secondary p-3 font-mono text-[13px] leading-5 text-fg transition focus:bg-surface"
            aria-label="Standing rules (AGENT.md)"
          />
        </section>
      </div>
    </div>
  );
}

function ManualAddMessageSection({ conversation, onAdded, onError }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [conversation?.id]);

  return (
    <div className="shrink-0 border-t border-line bg-surface">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="text-sm font-medium text-fg">Add a message</span>
        <ChevronDownIcon className={["h-4 w-4 shrink-0 text-fg-tertiary transition", open ? "rotate-180" : ""].join(" ")} />
      </button>
      <div className={open ? "px-4 pb-4" : "hidden"}>
        <ManualTranscriptComposer
          conversation={conversation}
          onAdded={onAdded}
          onError={onError}
        />
      </div>
    </div>
  );
}

function RightPane({ conversation, previewEmail = null, previewChat = null, actions, autonomousRules, onRulesSaved, onAuthorizationChanged, onManualTranscriptChanged, onError, threadRefreshKey, docked = true, onClose = null, isResizing, onResizeStart, onResizeMove, onResizeEnd, onResizeBy }) {
  const [threadEmails, setThreadEmails] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [activeTab, setActiveTab] = useState("messages");
  const [timelineNotice, setTimelineNotice] = useState("");
  const timelineScrollRef = useRef(null);
  const stickTimelineToBottomRef = useRef(true);
  const [authSettings, setAuthSettings] = useState({
    guardrails: DEFAULT_GUARDRAILS,
    maxSends: DEFAULT_MAX_SENDS,
    requireApproval: false,
    expiresInHours: "",
    silenceFollowUp: false,
    silenceFollowUpAfterHours: "48",
    silenceFollowUpMax: "1",
  });

  const hasSetupPreview = Boolean(previewEmail || previewChat?.space);

  useEffect(() => {
    if (previewEmail?.id || previewChat?.space?.name) setActiveTab("messages");
  }, [previewEmail?.id, previewChat?.space?.name]);

  useEffect(() => {
    stickTimelineToBottomRef.current = true;
    setTimelineNotice("");
  }, [conversation?.id]);

  useEffect(() => {
    if (!conversation) return;
    const limits = conversation.authorization_limits ?? {};
    setAuthSettings({
      guardrails: limits.guardrails ?? DEFAULT_GUARDRAILS,
      maxSends: limits.max_sends ?? DEFAULT_MAX_SENDS,
      requireApproval: Boolean(limits.require_approval),
      expiresInHours: "",
      silenceFollowUp: Boolean(limits.silence_follow_up),
      silenceFollowUpAfterHours: String(limits.silence_follow_up_after_hours ?? 48),
      silenceFollowUpMax: String(limits.silence_follow_up_max ?? 1),
    });
  }, [conversation?.id, conversation?.authorization_state]);

  useEffect(() => {
    const threadId = conversation?.selected_thread_id;
    if (!conversation || !conversationHasGmailThread(conversation)) {
      setThreadEmails([]);
      return undefined;
    }

    let cancelled = false;
    const loadThreadEmails = () => {
      fetchJson(`/api/agent/threads/${threadId}`)
        .then((payload) => {
          if (cancelled) return;
          setThreadEmails(payload.emails ?? []);
          setTimelineNotice("");
        })
        .catch((e) => {
          if (cancelled) return;
          setThreadEmails([]);
          // Not a toast: the saved snapshot is still shown, so a quiet inline note is enough.
          setTimelineNotice(describeError("Couldn't refresh the Gmail thread, so the saved copy is shown", e));
        });
    };

    loadThreadEmails();
    const intervalId = window.setInterval(loadThreadEmails, THREAD_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [conversation?.id, conversation?.mode, conversation?.selected_thread_id, threadRefreshKey]);

  useEffect(() => {
    const spaceName = conversation?.selected_space_name;
    const threadName = conversation?.selected_thread_name;
    if (!conversation || !isChatMode(conversation.mode) || !spaceName) {
      setChatMessages([]);
      return undefined;
    }

    let cancelled = false;
    const loadChatMessages = () => {
      const params = new URLSearchParams({ page_size: "50" });
      if (threadName) params.set("thread_name", threadName);

      fetchJson(`/api/agent/chat_messages/${spaceName}?${params.toString()}`)
        .then((payload) => {
          if (cancelled) return;
          setChatMessages(payload.messages ?? []);
          setTimelineNotice("");
        })
        .catch((e) => {
          if (cancelled) return;
          setChatMessages([]);
          setTimelineNotice(describeError("Couldn't refresh this Google Chat space, so the saved copy is shown", e));
        });
    };

    loadChatMessages();
    const intervalId = window.setInterval(loadChatMessages, THREAD_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    conversation?.id,
    conversation?.mode,
    conversation?.selected_space_name,
    conversation?.selected_thread_name,
    threadRefreshKey,
  ]);

  const userLanguage = useMemo(
    () => conversationUserLanguage(conversation, actions),
    [conversation, actions],
  );
  const translationConversationId = translatableConversationId(conversation);
  const timelineEntries = buildTimelineEntries(conversation, actions, threadEmails, chatMessages);

  useLayoutEffect(() => {
    if (activeTab !== "messages" || !conversation?.id || !stickTimelineToBottomRef.current) return;

    const root = timelineScrollRef.current;
    if (!root) return;

    const scrollToBottom = () => {
      if (!stickTimelineToBottomRef.current || !timelineScrollRef.current) return;
      timelineScrollRef.current.scrollTop = timelineScrollRef.current.scrollHeight;
    };

    scrollToBottom();
    const frame = window.requestAnimationFrame(scrollToBottom);
    return () => window.cancelAnimationFrame(frame);
  }, [activeTab, conversation?.id, timelineEntries.length]);

  const handleTimelineScroll = () => {
    const root = timelineScrollRef.current;
    if (!root) return;
    stickTimelineToBottomRef.current =
      root.scrollHeight - root.scrollTop - root.clientHeight < 80;
  };

  const paneSubject = (() => {
    if (!conversation) {
      if (previewChat?.space) return spaceDisplayTitle(previewChat.space);
      return previewEmail?.subject || null;
    }
    if (isManualMode(conversation.mode)) {
      return conversation.recipient || conversation.title || "Untitled conversation";
    }
    if (isChatMode(conversation.mode)) {
      return conversation.selected_space_name || conversation.title || "Untitled conversation";
    }
    return (
      conversation.subject
      || conversation.selected_email_snapshot?.subject
      || conversation.title
      || "Untitled conversation"
    );
  })();

  const previewEmailEntry = previewEmail
    ? {
        id: `preview-email-${previewEmail.id}`,
        type: "email",
        timestamp: previewEmail.date,
        email: previewEmail,
        isSelected: true,
      }
    : null;

  const previewChatEntries = previewChat?.space
    ? [...(previewChat.messages ?? [])]
        .sort((a, b) => timestampValue(a.create_time) - timestampValue(b.create_time))
        .slice(-1)
        .map((message) => ({
          id: `preview-chat-${message.name}`,
          type: "chat",
          timestamp: message.create_time,
          chatMessage: message,
          isSelected: true,
        }))
    : [];

  const setupPreviewHint = previewChat?.space
    ? "Preview of the latest message in this space. Start the conversation to begin drafting a reply."
    : "Preview of the email you selected. Start the conversation to begin drafting a reply.";

  const closeButton = (
    <button type="button" className={GHOST_ICON_BUTTON_CLASS} onClick={onClose} aria-label="Close conversation details">
      <CloseIcon className="h-4 w-4" />
    </button>
  );

  return (
    <aside className={["relative flex h-full min-h-0 flex-col bg-surface", docked ? "border-l border-line" : ""].join(" ")}>
      {docked ? (
        <button
          type="button"
          aria-label="Resize right pane"
          title="Drag to resize right pane"
          className={[
            "absolute -left-1 top-0 z-10 h-full w-2 cursor-col-resize appearance-none border-0 p-0 transition focus-ring-none",
            isResizing ? "bg-line-strong" : "hover:bg-line-strong/70 focus-visible:bg-focus",
          ].join(" ")}
          onPointerDown={onResizeStart}
          onMouseDown={onResizeStart}
          onDragStart={(event) => onResizeStart(event, { preventDefault: false })}
          onDrag={(event) => onResizeMove(event.clientX)}
          onDragEnd={onResizeEnd}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              onResizeBy(24);
            } else if (event.key === "ArrowRight") {
              event.preventDefault();
              onResizeBy(-24);
            }
          }}
        />
      ) : null}
      <div className="border-b border-line">
        {conversation || hasSetupPreview ? (
          <div className="border-b border-line px-4 py-3">
            {conversation ? (
              <>
                <div className="flex items-start justify-between gap-2">
                  {paneSubject ? (
                    <p className="min-w-0 truncate text-sm font-semibold leading-5 text-fg" title={paneSubject}>
                      {paneSubject}
                    </p>
                  ) : <span />}
                  {onClose ? closeButton : null}
                </div>
                <FollowUpGoalDisplay
                  goal={conversation.goal}
                  successCriteria={conversation.success_criteria}
                  achieved={conversation.goal_achieved}
                  achievedAt={conversation.goal_achieved_at}
                  className="mt-3"
                />
                <div className="mt-2.5">
                  <AuthorizationToggle
                    conversation={conversation}
                    settings={authSettings}
                    onChanged={onAuthorizationChanged}
                    onError={onError}
                    compact
                  />
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start justify-between gap-2">
                  {paneSubject ? (
                    <p className="min-w-0 text-sm font-semibold leading-5 text-fg">{paneSubject}</p>
                  ) : <span />}
                  {onClose ? closeButton : null}
                </div>
                <p className={[paneSubject ? "mt-2" : "", "text-[13px] leading-5 text-fg-tertiary"].filter(Boolean).join(" ")}>
                  {setupPreviewHint}
                </p>
              </>
            )}
          </div>
        ) : null}
        <div className="flex h-14 items-center justify-between gap-2 px-4">
          <div className="inline-flex max-w-full rounded-full bg-surface-tertiary p-0.5" role="tablist" aria-label="Conversation side panel">
            {[
              { id: "messages", label: "Messages" },
              { id: "rules", label: "Rules" },
              { id: "silence", label: "Follow up" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={[
                  "rounded-full px-3 py-1 text-[13px]/4 font-medium transition",
                  activeTab === tab.id
                    ? "bg-surface text-fg shadow-sm"
                    : "text-fg-secondary hover:text-fg",
                ].join(" ")}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Without a conversation header the close control lives on the tabs row instead. */}
          {onClose && !(conversation || hasSetupPreview) ? closeButton : null}
        </div>
      </div>
      {activeTab === "rules" ? (
        <RulesPane
          conversation={conversation}
          authSettings={authSettings}
          onAuthSettingsChange={setAuthSettings}
          autonomousRules={autonomousRules}
          onAuthorizationChanged={onAuthorizationChanged}
          onSaved={onRulesSaved}
          onError={onError}
        />
      ) : activeTab === "silence" ? (
        <SilenceFollowUpPane
          conversation={conversation}
          settings={authSettings}
          onSettingsChange={setAuthSettings}
          onChanged={onAuthorizationChanged}
          onError={onError}
        />
      ) : !conversation && !hasSetupPreview ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-tertiary text-fg-secondary">
            <MailIcon className="h-5 w-5" />
          </span>
          <p className="max-w-xs text-sm leading-6 text-fg-tertiary">
            Create or select a conversation to see the message thread, authorization scope, and audit trail.
          </p>
        </div>
      ) : !conversation && hasSetupPreview ? (
        <div className="gpt-scroll min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <section>
            <div className="mb-3 flex items-center justify-between">
              <p className={SECTION_LABEL_CLASS}>Messages</p>
              <span className={statusBadgeClass("neutral", "text-[13px]/4")}>Preview</span>
            </div>
            <div className="space-y-3">
              {previewEmailEntry ? (
                <TimelineEmailCard entry={previewEmailEntry} conversation={null} />
              ) : null}
              {previewChat?.loading ? (
                <p className="py-6 text-center text-sm text-fg-tertiary">Loading messages…</p>
              ) : null}
              {!previewChat?.loading && previewChat?.space && previewChatEntries.length === 0 ? (
                <p className="py-6 text-center text-sm text-fg-tertiary">No recent messages in this space.</p>
              ) : null}
              {previewChatEntries.map((entry) => (
                <TimelineChatCard key={entry.id} entry={entry} conversation={null} defaultExpanded />
              ))}
            </div>
          </section>
        </div>
      ) : (
        <div
          ref={timelineScrollRef}
          className="gpt-scroll min-h-0 flex-1 space-y-4 overflow-y-auto p-4"
          onScroll={handleTimelineScroll}
        >
          <section>
            <div className="mb-3 flex items-center justify-between">
              <p className={SECTION_LABEL_CLASS}>Timeline</p>
              <span className="text-[13px]/4 text-fg-tertiary">
                {timelineEntries.length} {timelineEntries.length === 1 ? "event" : "events"}
              </span>
            </div>
            {timelineNotice ? (
              <p className="mb-3 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-[13px] leading-5 text-fg-secondary" role="status">
                <AlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
                <span>{timelineNotice}</span>
              </p>
            ) : null}
            <div className="space-y-3">
              {isManualMode(conversation?.mode) && !timelineEntries.some((entry) => entry.type === "manual") ? (
                <p className="py-6 text-center text-sm text-fg-tertiary">
                  No recent messages in this space.
                </p>
              ) : null}
              {timelineEntries.map((entry) => {
                const translation = {
                  userLanguage,
                  conversationId: translationConversationId,
                  onError,
                };
                if (entry.type === "email") {
                  return <TimelineEmailCard key={entry.id} entry={entry} conversation={conversation} {...translation} />;
                }
                if (entry.type === "chat") {
                  return <TimelineChatCard key={entry.id} entry={entry} conversation={conversation} {...translation} />;
                }
                if (entry.type === "manual") {
                  return <TimelineManualCard key={entry.id} entry={entry} conversation={conversation} {...translation} />;
                }
                return <TimelineActionCard key={entry.id} entry={entry} {...translation} />;
              })}
            </div>
          </section>
        </div>
      )}
      {conversation && isManualMode(conversation.mode) && activeTab === "messages" ? (
        <ManualAddMessageSection
          key={conversation.id}
          conversation={conversation}
          onAdded={onManualTranscriptChanged}
          onError={onError}
        />
      ) : null}
    </aside>
  );
}

function translationRequestText(text, subject, userLanguage) {
  const body = readableTranslationText(text);
  const title = readableTranslationText(subject);
  if (!title || body.includes(title)) return body;
  if (userLanguage && !translationSourceLanguage(title, userLanguage)) return body;
  return `${title}\n\n${body}`;
}

function TimelineMessageTranslation({
  text,
  subject = "",
  userLanguage,
  conversationId,
  onError,
  controlId,
  panelClassName = "bg-surface-secondary",
}) {
  const requestText = translationRequestText(text, subject, userLanguage);
  const sourceLanguage = translationSourceLanguage(requestText, userLanguage);
  const canOffer = Boolean(conversationId && sourceLanguage && userLanguage);
  const userLabel = languageLabel(userLanguage);
  const sourceLabel = languageLabel(sourceLanguage);
  const [showing, setShowing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const cacheRef = useRef(new Map());
  const translatedText = cacheRef.current.get(requestText) || "";
  const visible = Boolean(showing && translatedText);

  useEffect(() => {
    setShowing(false);
    setDismissed(false);
  }, [requestText, userLanguage]);

  if (!canOffer || dismissed) return null;

  const showTranslation = async () => {
    if (translating) return;
    if (cacheRef.current.get(requestText)) {
      setShowing(true);
      return;
    }

    setTranslating(true);
    try {
      const payload = await fetchJson(`/api/agent/conversations/${conversationId}/content_translation`, {
        method: "POST",
        body: JSON.stringify({
          text: requestText,
          target_language: userLanguage,
          source_language: sourceLanguage,
        }),
      });
      if (!payload.available || !String(payload.text || "").trim()) {
        setDismissed(true);
        return;
      }
      cacheRef.current.set(requestText, payload.text);
      setShowing(true);
    } catch (error) {
      onError?.(describeError(`Couldn't show this message in ${userLabel}`, error));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="mt-2">
      {visible ? (
        <div id={controlId} className={["rounded-lg px-3 py-2.5", panelClassName].join(" ")}>
          <p className="text-xs font-medium text-fg-tertiary">{userLabel}</p>
          <p className="mt-1 whitespace-pre-wrap text-[14px] leading-5 text-fg-secondary">{translatedText}</p>
          <p className="mt-2 text-xs leading-5 text-fg-tertiary">
            {`Shown in ${userLabel} for your review. The original message stays in ${sourceLabel}.`}
          </p>
        </div>
      ) : null}
      <button
        type="button"
        className={visible ? ["mt-2", BTN_LINK_13].join(" ") : BTN_LINK_13}
        onClick={() => (visible ? setShowing(false) : showTranslation())}
        disabled={translating}
        aria-expanded={visible}
        aria-controls={controlId}
      >
        {translating
          ? "Translating..."
          : visible
            ? `Hide ${userLabel}`
            : `Show in ${userLabel}`}
      </button>
    </div>
  );
}

function TimelineEmailCard({ entry, conversation, userLanguage = "", conversationId = null, onError }) {
  const entryEmail = entry.email ?? conversation?.selected_email_snapshot;
  const [email, setEmail] = useState(entryEmail);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [bodyLoading, setBodyLoading] = useState(false);
  const showsGmailMessage = Boolean(email?.id);
  const isSelected = entry.isSelected ?? true;
  const previewText = (email?.snippet || email?.body_text || "").trim();

  useEffect(() => {
    setEmail(entryEmail);
    setBodyExpanded(false);
  }, [entryEmail]);

  useEffect(() => {
    const messageId = entryEmail?.id;
    if (!bodyExpanded || !messageId || email?.body_html?.trim()) {
      return undefined;
    }

    let cancelled = false;
    setBodyLoading(true);
    fetchJson(`/api/agent/inbox/${messageId}`)
      .then((payload) => {
        if (!cancelled && payload.email) {
          setEmail((current) => ({ ...(current ?? entryEmail ?? {}), ...payload.email }));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBodyLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [bodyExpanded, email?.body_html, entryEmail]);

  const cardLabel = () => {
    if (!showsGmailMessage) return "Email requested";
    if (!conversation) return "Selected email";
    if (conversation.mode === "new_email") {
      return isSelected ? "Email sent" : "In thread";
    }
    return isSelected ? "Email received" : "Earlier in thread";
  };

  return (
    <div className="rounded-xl border border-line bg-surface-secondary p-3 text-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[13px]/4 font-medium text-fg">
          <MailIcon className="h-3.5 w-3.5 text-fg-tertiary" />
          {cardLabel()}
        </p>
        <time className="text-right text-[13px]/4 text-fg-tertiary">{formatTimelineTimestamp(entry.timestamp)}</time>
      </div>
      {showsGmailMessage ? (
        <div>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]/4 text-fg-secondary">
            <dt className="font-medium text-fg-tertiary">From</dt>
            <dd className="break-words">{email.from || "Unknown"}</dd>
            <dt className="font-medium text-fg-tertiary">To</dt>
            <dd className="break-words">{email.to || "Unknown"}</dd>
            {email.cc ? (
              <>
                <dt className="font-medium text-fg-tertiary">Cc</dt>
                <dd className="break-words">{email.cc}</dd>
              </>
            ) : null}
          </dl>
          {bodyExpanded ? (
            bodyLoading ? (
              <p className="mt-3 text-[13px]/4 text-fg-tertiary">Loading email…</p>
            ) : (
              <EmailHtmlBody
                html={email.body_html}
                plainText={email.body_text || email.snippet}
              />
            )
          ) : (
            <button
              type="button"
              className="mt-3 w-full rounded-lg bg-surface p-3 text-left transition hover:bg-surface-hover"
              onClick={() => {
                if (!email?.body_html?.trim()) setBodyLoading(true);
                setBodyExpanded(true);
              }}
            >
              <p className="line-clamp-5 whitespace-pre-wrap text-[14px] leading-5 text-fg-secondary">
                {previewText || "No email preview available. Click to load full message."}
              </p>
              <span className={["mt-2 inline-block", BTN_LINK_13].join(" ")}>
                Show full email
              </span>
            </button>
          )}
          {bodyExpanded && !bodyLoading ? (
            <button
              type="button"
              className={["mt-2", BTN_LINK_13].join(" ")}
              onClick={() => setBodyExpanded(false)}
            >
              Collapse to preview
            </button>
          ) : null}
          <TimelineMessageTranslation
            text={emailPlainText(email)}
            subject={email?.subject || conversation?.subject || ""}
            userLanguage={userLanguage}
            conversationId={conversationId}
            onError={onError}
            controlId={`timeline-translation-${entry.id}`}
            panelClassName="bg-surface"
          />
        </div>
      ) : (
        <div className="text-sm text-fg-secondary">
          <p className="text-[13px]/4 text-fg-tertiary">New email to</p>
          <p className="mt-1 break-words text-sm text-fg">{conversation.recipient || "Recipient not set"}</p>
        </div>
      )}
    </div>
  );
}

function TimelineManualCard({ entry, conversation, userLanguage = "", conversationId = null, onError }) {
  const message = entry.manualMessage;
  const outbound = message?.direction === "outbound";
  const fromLabel = outbound
    ? (message?.sender_name || "You")
    : (message?.sender_name || conversation?.recipient || "Them");

  return (
    <div className="rounded-xl border border-line bg-surface-secondary p-3 text-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[13px]/4 font-medium text-fg">
          <ChatIcon className="h-3.5 w-3.5 text-fg-tertiary" />
          {outbound ? "Message sent" : "Message received"}
        </p>
        {entry.timestamp ? (
          <time className="text-right text-[13px]/4 text-fg-tertiary">{formatTimelineTimestamp(entry.timestamp)}</time>
        ) : null}
      </div>
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]/4 text-fg-secondary">
        <dt className="font-medium text-fg-tertiary">From</dt>
        <dd className="break-words">{fromLabel}</dd>
      </dl>
      <div className="mt-3 rounded-lg bg-surface p-3">
        <CollapsibleTimelineContent
          content={message?.body || "No message content."}
          defaultExpanded={!conversation}
          expandLabel="Show full message"
          collapseLabel="Collapse to preview"
          userLanguage={userLanguage}
          conversationId={conversationId}
          onError={onError}
          controlId={`timeline-translation-${entry.id}`}
        />
      </div>
    </div>
  );
}

function TimelineChatCard({ entry, conversation, defaultExpanded = false, userLanguage = "", conversationId = null, onError }) {
  const chatMessage = entry.chatMessage ?? conversation?.selected_chat_snapshot;
  const isSelected = entry.isSelected ?? true;
  const cardLabel = !conversation
    ? "Latest message"
    : (isSelected ? "Message received" : "Earlier in thread");
  const fromLabel = senderDisplayTitle(chatMessage?.sender);
  const fromEmail = chatMessage?.sender?.email;

  return (
    <div className="rounded-xl border border-line bg-surface-secondary p-3 text-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <p className="flex items-center gap-1.5 text-[13px]/4 font-medium text-fg">
          <ChatIcon className="h-3.5 w-3.5 text-fg-tertiary" />
          {cardLabel}
        </p>
        <time className="text-right text-[13px]/4 text-fg-tertiary">{formatTimelineTimestamp(entry.timestamp)}</time>
      </div>
      <div>
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-[13px]/4 text-fg-secondary">
          <dt className="font-medium text-fg-tertiary">From</dt>
          <dd className="break-words">
            {fromEmail && fromLabel !== fromEmail
              ? `${fromLabel} <${fromEmail}>`
              : fromLabel}
          </dd>
        </dl>
        <div className="mt-3 rounded-lg bg-surface p-3">
          <CollapsibleTimelineContent
            content={chatMessage?.text || "No message content available."}
            defaultExpanded={defaultExpanded}
            expandLabel="Show full message"
            collapseLabel="Collapse to preview"
            userLanguage={userLanguage}
            conversationId={conversationId}
            onError={onError}
            controlId={`timeline-translation-${entry.id}`}
          />
        </div>
      </div>
    </div>
  );
}

function CollapsibleTimelineContent({
  content,
  defaultExpanded = false,
  expandLabel = "Show full content",
  collapseLabel = "Collapse to preview",
  userLanguage = "",
  conversationId = null,
  onError,
  controlId,
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const canToggle = contentNeedsCollapse(content);
  const showFull = !canToggle || expanded;

  return (
    <div>
      {canToggle && !showFull ? (
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setExpanded(true)}
        >
          <p className="line-clamp-5 whitespace-pre-wrap text-[14px] leading-5 text-fg-secondary">
            {content}
          </p>
          <span className={["mt-2 inline-block", BTN_LINK_13].join(" ")}>
            {expandLabel}
          </span>
        </button>
      ) : (
        <>
          <p className="whitespace-pre-wrap text-[14px] leading-5 text-fg-secondary">
            {content}
          </p>
          {canToggle ? (
            <button
              type="button"
              className={["mt-2", BTN_LINK_13].join(" ")}
              onClick={() => setExpanded(false)}
            >
              {collapseLabel}
            </button>
          ) : null}
        </>
      )}
      <TimelineMessageTranslation
        text={content}
        userLanguage={userLanguage}
        conversationId={conversationId}
        onError={onError}
        controlId={controlId}
      />
    </div>
  );
}

const actionStatusTone = (status) => {
  if (status === "completed") return "success";
  if (status === "failed" || status === "blocked") return "warning";
  return "neutral";
};

const isAuthorizationAction = (action) =>
  action.action_type === "authorization_granted" || action.action_type === "authorization_revoked";

function TimelineGoalSnippet({ goal, successCriteria = [], achieved = false }) {
  const [expanded, setExpanded] = useState(false);
  const criteria = successCriteriaList(successCriteria);

  if (!goal) return null;

  if (!expanded) {
    return (
      <button
        type="button"
        className="mt-2 w-full text-left"
        onClick={() => setExpanded(true)}
        title={criteria.length > 0 ? "Show full goal and success criteria" : "Show full goal"}
      >
        <p className={SECTION_LABEL_CLASS}>Goal</p>
        <p className="line-clamp-1 text-[13px] leading-5 text-fg-secondary">{goal}</p>
        {criteria.length > 0 ? (
          <p className="text-[13px] leading-5 text-fg-tertiary">
            {criteria.length} success {criteria.length === 1 ? "criterion" : "criteria"}
          </p>
        ) : null}
      </button>
    );
  }

  return (
    <div className="mt-2">
      <p className={SECTION_LABEL_CLASS}>Goal</p>
      <p className="whitespace-pre-wrap text-[13px] leading-5 text-fg-secondary">{goal}</p>
      <SuccessCriteriaList
        criteria={criteria}
        className="mt-2"
        textClass="text-[13px] leading-5 text-fg-secondary"
        achieved={achieved}
      />
      <button
        type="button"
        className={["mt-1", BTN_LINK_13].join(" ")}
        onClick={() => setExpanded(false)}
      >
        Collapse goal
      </button>
    </div>
  );
}

function TimelineActionCard({ entry, userLanguage = "", conversationId = null, onError }) {
  const { action } = entry;
  const isChatAction = action.action_type?.includes("chat");
  const messageContent = action.payload?.body || action.payload?.text || action.payload?.reason
    || (isGoalAchievedAction(action) ? action.payload?.summary : "");
  const expandLabel = isChatAction ? "Show full message" : isGoalAchievedAction(action) ? "Show full report" : "Show full email";
  const showsGoal = isAuthorizationAction(action) || isGoalAction(action) || isGoalAchievedAction(action);
  const authorizationGoal = showsGoal ? (action.payload?.goal || "") : "";
  const goalCriteria = showsGoal ? action.payload?.success_criteria : [];

  return (
    <div className="rounded-xl border border-line bg-surface p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[13px]/4 font-medium text-fg">{actionLabel(action)}</p>
          <time className="mt-0.5 block text-[13px]/4 text-fg-tertiary">{formatTimelineTimestamp(entry.timestamp)}</time>
        </div>
        <span className={statusBadgeClass(actionStatusTone(action.status), "text-[13px]/4")}>{actionStatusLabel(action.status)}</span>
      </div>
      {action.payload?.subject && <p className="mt-2 text-sm font-medium text-fg">{action.payload.subject}</p>}
      {authorizationGoal ? (
        <TimelineGoalSnippet goal={authorizationGoal} successCriteria={goalCriteria} achieved={isGoalAchievedAction(action)} />
      ) : null}
      {messageContent ? (
        <div className="mt-2">
          <CollapsibleTimelineContent
            content={messageContent}
            expandLabel={expandLabel}
            collapseLabel="Collapse to preview"
            userLanguage={userLanguage}
            conversationId={conversationId}
            onError={onError}
            controlId={`timeline-translation-${entry.id}`}
          />
        </div>
      ) : null}
      {action.error_message && <p className="mt-2 text-[13px]/4 text-danger">{action.error_message}</p>}
    </div>
  );
}

let assistantPageTracked = false;
let emailAgentRoot;

function mountEmailAgent() {
  const rootElement = document.getElementById("email-agent-root");
  if (!rootElement || rootElement.dataset.reactMounted === "true") return;

  rootElement.dataset.reactMounted = "true";
  emailAgentRoot = createRoot(rootElement);
  emailAgentRoot.render(
    <StrictMode>
      <EmailAgentApp rootElement={rootElement} />
    </StrictMode>
  );
}

document.addEventListener("turbo:load", mountEmailAgent);
document.addEventListener("turbo:before-cache", () => {
  emailAgentRoot?.unmount();
  emailAgentRoot = undefined;
  assistantPageTracked = false;

  const rootElement = document.getElementById("email-agent-root");
  if (rootElement) delete rootElement.dataset.reactMounted;
});
mountEmailAgent();
